(() => {
  const storageKey = 'akibart:favorites';
  const readFavorites = () => {
    try {
      const handles = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      return Array.isArray(handles) ? [...new Set(handles.filter((handle) => typeof handle === 'string'))] : [];
    } catch {
      return [];
    }
  };

  const writeFavorites = (handles) => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(handles));
    } catch {
      // Storage can be disabled by the browser; the page remains usable without it.
    }
    document.dispatchEvent(new CustomEvent('akibart:favorites-change'));
  };

  const setFavoriteState = () => {
    const favorites = readFavorites();
    document.querySelectorAll('[data-akibart-favorite]').forEach((button) => {
      const isFavorite = favorites.includes(button.dataset.productHandle);
      button.setAttribute('aria-pressed', String(isFavorite));
      button.classList.toggle('is-favorite', isFavorite);
      const productTitle = button.dataset.productTitle || button.getAttribute('aria-label')?.replace(/をお気に入りに追加$/, '') || 'この商品';
      button.setAttribute('aria-label', `${productTitle}をお気に入り${isFavorite ? 'から削除' : 'に追加'}`);
    });

    document.querySelectorAll('[data-akibart-favorites-count]').forEach((count) => {
      count.textContent = String(favorites.length);
      count.hidden = favorites.length === 0;
    });
  };

  const toggleFavorite = (handle) => {
    if (!handle) return;
    const favorites = readFavorites();
    const next = favorites.includes(handle) ? favorites.filter((item) => item !== handle) : [...favorites, handle];
    writeFavorites(next);
    setFavoriteState();
  };

  const createFavoriteCard = (product, rootUrl, sectionId) => {
    const article = document.createElement('article');
    article.className = 'akibart-product-card';

    const media = document.createElement('div');
    media.className = 'akibart-product-card__media';
    const link = document.createElement('a');
    link.className = 'akibart-product-card__image-link';
    link.href = `${rootUrl}products/${encodeURIComponent(product.handle)}`;
    link.setAttribute('aria-label', product.title);
    link.draggable = false;

    if (product.featured_image) {
      const image = document.createElement('img');
      image.className = 'akibart-product-card__primary-image';
      image.src = product.featured_image;
      image.alt = product.title;
      image.loading = 'lazy';
      image.draggable = false;
      link.append(image);

      const secondaryImageSource = Array.isArray(product.images) ? product.images[1] : '';
      if (secondaryImageSource && secondaryImageSource !== product.featured_image) {
        const secondaryImage = document.createElement('img');
        secondaryImage.className = 'akibart-product-card__secondary-image';
        secondaryImage.src = secondaryImageSource;
        secondaryImage.alt = product.title;
        secondaryImage.loading = 'lazy';
        secondaryImage.draggable = false;
        secondaryImage.setAttribute('aria-hidden', 'true');
        link.append(secondaryImage);
      }
    }

    media.append(link);
    const labelsContainer = document.createElement('div');
    labelsContainer.className = 'akibart-product-card__labels';
    const labelsByTag = {
      NEW: ['NEW', 'akibart-label--new'],
      '予約': ['予約', 'akibart-label--reservation'],
      '限定': ['限定', 'akibart-label--limited'],
      '特典付き': ['特典付き', 'akibart-label--bonus'],
    };
    const productTags = Array.isArray(product.tags)
      ? product.tags
      : typeof product.tags === 'string'
        ? product.tags.split(',')
        : [];
    productTags.forEach((tag) => {
      const label = labelsByTag[String(tag).trim()];
      if (!Array.isArray(label)) return;
      const badge = document.createElement('span');
      badge.className = `akibart-label ${label[1]}`;
      badge.textContent = label[0];
      labelsContainer.append(badge);
    });
    media.append(labelsContainer);

    if (!product.available) {
      const soldOut = document.createElement('span');
      soldOut.className = 'akibart-product-card__sold-out';
      soldOut.textContent = 'SOLD OUT';
      media.append(soldOut);
    }

    const variants = Array.isArray(product.variants) ? product.variants : [];
    if (product.available && variants.length === 1 && variants[0].available) {
      const quickAction = document.createElement('div');
      quickAction.className = 'akibart-product-card__quick-action';
      const productForm = document.createElement('product-form-component');
      productForm.className = 'akibart-product-card__quick-form';
      productForm.dataset.sectionId = sectionId || '';
      productForm.dataset.productId = String(product.id || '');
      productForm.dataset.productUrl = link.href;
      productForm.dataset.akibartAjaxCart = 'delayed';
      productForm.dataset.akibartCartFeedback = '';
      productForm.dataset.quantityDefault = '1';

      const liveRegion = document.createElement('span');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('aria-live', 'assertive');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('ref', 'liveRegion');
      productForm.append(liveRegion);

      const form = document.createElement('form');
      form.className = 'akibart-product-card__quick-cart-form';
      form.method = 'post';
      form.action = `${rootUrl}cart/add`;
      form.acceptCharset = 'UTF-8';
      form.dataset.type = 'add-to-cart-form';

      const variantInput = document.createElement('input');
      variantInput.type = 'hidden';
      variantInput.name = 'id';
      variantInput.value = String(variants[0].id);
      variantInput.setAttribute('ref', 'variantId');
      form.append(variantInput);

      const errorMessage = document.createElement('span');
      errorMessage.className = 'akibart-product-card__quick-error product-form-text__error hidden';
      errorMessage.setAttribute('role', 'status');
      errorMessage.setAttribute('ref', 'addToCartTextError');
      form.append(errorMessage);

      const addToCart = document.createElement('add-to-cart-component');
      addToCart.setAttribute('ref', 'addToCartButtonContainer');
      const addButton = document.createElement('button');
      addButton.className = 'akibart-product-card__quick-button';
      addButton.type = 'submit';
      addButton.name = 'add';
      addButton.setAttribute('ref', 'addToCartButton');
      addButton.dataset.akibartAddButton = '';
      addButton.dataset.labelAvailable = 'カートに追加する';
      addButton.dataset.labelUnavailable = '売り切れ';
      addButton.setAttribute('aria-label', `${product.title}をカートに追加`);
      addButton.innerHTML = '<span class="akibart-product-card__quick-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3.392 6.875h13.216v8.016c0 .567-.224 1.112-.624 1.513-.4.402-.941.627-1.506.627H5.522a2.13 2.13 0 0 1-1.506-.627 2.15 2.15 0 0 1-.624-1.513zM8.818 2.969h2.333c.618 0 1.211.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.313c0-.622.246-1.218.683-1.658a2.33 2.33 0 0 1 1.65-.686"/></svg></span><span class="akibart-product-card__quick-label">カートに追加する</span>';
      addToCart.append(addButton);
      form.append(addToCart);
      productForm.append(form);
      quickAction.append(productForm);
      media.append(quickAction);
    } else if (product.available && variants.length > 1) {
      const quickAction = document.createElement('div');
      quickAction.className = 'akibart-product-card__quick-action';
      const chooseVariant = document.createElement('a');
      chooseVariant.className = 'akibart-product-card__quick-button';
      chooseVariant.href = link.href;
      chooseVariant.dataset.akibartCardAction = '';
      chooseVariant.draggable = false;
      chooseVariant.setAttribute('aria-label', `${product.title}の種類を選んでカートに追加`);
      chooseVariant.innerHTML = '<span class="akibart-product-card__quick-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3.392 6.875h13.216v8.016c0 .567-.224 1.112-.624 1.513-.4.402-.941.627-1.506.627H5.522a2.13 2.13 0 0 1-1.506-.627 2.15 2.15 0 0 1-.624-1.513zM8.818 2.969h2.333c.618 0 1.211.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.313c0-.622.246-1.218.683-1.658a2.33 2.33 0 0 1 1.65-.686"/></svg></span><span class="akibart-product-card__quick-label">種類を選択</span>';
      quickAction.append(chooseVariant);
      media.append(quickAction);
    } else {
      const quickAction = document.createElement('div');
      quickAction.className = 'akibart-product-card__quick-action';
      const unavailableButton = document.createElement('button');
      unavailableButton.className = 'akibart-product-card__quick-button';
      unavailableButton.type = 'button';
      unavailableButton.disabled = true;
      unavailableButton.setAttribute('aria-label', `${product.title}は売り切れです`);
      unavailableButton.innerHTML = '<span class="akibart-product-card__quick-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3.392 6.875h13.216v8.016c0 .567-.224 1.112-.624 1.513-.4.402-.941.627-1.506.627H5.522a2.13 2.13 0 0 1-1.506-.627 2.15 2.15 0 0 1-.624-1.513zM8.818 2.969h2.333c.618 0 1.211.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.313c0-.622.246-1.218.683-1.658a2.33 2.33 0 0 1 1.65-.686"/></svg></span><span class="akibart-product-card__quick-label">売り切れ</span>';
      quickAction.append(unavailableButton);
      media.append(quickAction);
    }

    const button = document.createElement('button');
    button.className = 'akibart-favorite-button';
    button.type = 'button';
    button.dataset.akibartFavorite = '';
    button.dataset.productHandle = product.handle;
    button.dataset.productTitle = product.title;
    button.setAttribute('aria-label', `${product.title}をお気に入りから削除`);
    button.setAttribute('aria-pressed', 'true');
    button.innerHTML = '<span aria-hidden="true">♡</span>';
    media.append(button);
    article.append(media);

    const details = document.createElement('div');
    details.className = 'akibart-product-card__details';
    const releaseDateValue = product.release_date || product.metafields?.custom?.release_date?.value || product.metafields?.custom?.release_date;
    if (releaseDateValue) {
      const releaseDate = new Date(releaseDateValue);
      if (!Number.isNaN(releaseDate.getTime())) {
        const releaseDateElement = document.createElement('time');
        releaseDateElement.className = 'akibart-release-date';
        releaseDateElement.dateTime = releaseDate.toISOString().slice(0, 10);
        releaseDateElement.textContent = `${new Intl.DateTimeFormat('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' }).format(releaseDate)}発売`;
        details.append(releaseDateElement);
      }
    }

    const title = document.createElement('a');
    title.className = 'akibart-product-card__title';
    title.href = link.href;
    title.textContent = product.title;
    details.append(title);

    const price = document.createElement('p');
    price.className = 'akibart-product-card__price';
    const currency = window.Shopify?.currency?.active || 'JPY';
    price.textContent = new Intl.NumberFormat('ja-JP', { style: 'currency', currency }).format(Number(product.price || 0) / 100);
    const priceRow = document.createElement('div');
    priceRow.className = 'akibart-product-card__price-row';
    priceRow.append(price);
    const quickAction = media.querySelector('.akibart-product-card__quick-action');
    if (quickAction) priceRow.append(quickAction);
    details.append(priceRow);
    article.append(details);
    return article;
  };

  const renderFavoritesPage = async (section) => {
    const list = section.querySelector('[data-akibart-favorites-list]');
    const empty = section.querySelector('[data-akibart-favorites-empty]');
    if (!list || !empty) return;

    const handles = readFavorites();
    list.replaceChildren();
    empty.hidden = handles.length > 0;
    if (!handles.length) return;

    const rootUrl = section.dataset.rootUrl || '/';
    const products = await Promise.all(handles.map(async (handle) => {
      try {
        const response = await fetch(`${rootUrl}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
        return response.ok ? await response.json() : null;
      } catch {
        return null;
      }
    }));

    products.filter(Boolean).forEach((product) => list.append(createFavoriteCard(product, rootUrl, section.dataset.sectionId)));
    empty.hidden = list.childElementCount > 0;
    if (list.childElementCount === 0 && handles.length > 0) {
      empty.textContent = '商品情報を読み込めませんでした。';
      empty.hidden = false;
    }
    setFavoriteState();
  };

  const initializeSlider = (slider) => {
    const track = slider.querySelector('[data-akibart-slider-track]');
    const slides = [...slider.querySelectorAll('[data-akibart-slide]')];
    const viewport = slider.querySelector('[data-akibart-slider-viewport]');
    const thumbnailViewport = slider.querySelector('[data-akibart-thumbnail-viewport]');
    const thumbnailTrack = slider.querySelector('[data-akibart-thumbnails]');
    const thumbnails = thumbnailTrack ? [...thumbnailTrack.querySelectorAll('[data-akibart-thumbnail]')] : [];
    if (!track || !viewport || slides.length === 0) return;

    const desktopMode = window.matchMedia('(min-width: 750px)');
    const canUseCarousel = () => slides.length > 1 && (!desktopMode.matches || slider.classList.contains('akibart-home__hero--coverflow'));

    slider.querySelectorAll('a[href], img').forEach((element) => { element.draggable = false; });
    slider.addEventListener('dragstart', (event) => event.preventDefault());

    const dots = [...slider.querySelectorAll('[data-akibart-slide-to]')].filter((control) => !control.hasAttribute('data-akibart-thumbnail'));
    const autoplayEnabled = slider.dataset.akibartAutoplay !== 'false';
    const parsedInterval = Number.parseInt(slider.dataset.akibartInterval || '4000', 10);
    const autoplayInterval = Math.min(10000, Math.max(2000, Number.isFinite(parsedInterval) ? parsedInterval : 4000));
    let index = 0;
    let mobileIndex = slides.length;
    let autoplayTimer = null;
    let animationTimer = null;
    let isAnimating = false;
    let isHovered = false;
    let isFocusVisible = false;
    let dragging = false;
    let pointerState = null;
    let mousePointerPosition = null;
    let ignoreLinkClickUntil = 0;
    let ignoreThumbnailClickUntil = 0;
    const queuedActions = [];
    let pendingCoverflowExit = false;
    const usesContinuousCoverflow = () => desktopMode.matches && slides.length > 2
      && slider.hasAttribute('data-akibart-hero-slider') && slider.classList.contains('akibart-home__hero--coverflow');
    let coverflowPosition = 0;
    let coverflowTarget = 0;
    let coverflowFrame = null;
    let coverflowWidth = 0;
    let coverflowThumbnailOrigin = 0;
    let coverflowThumbnailPitch = 0;

    // One continuous position drives both gestures and animation, including stacking.
    const renderCoverflow = (position) => {
      coverflowPosition = position;
      const width = coverflowWidth || (coverflowWidth = slides[0].offsetWidth || viewport.clientWidth);
      const lastVisible = slides.length >= 5 ? 2 : 1;
      // Odd counts cross the loop seam halfway between tiers. Hide there before
      // changing sides, so a wrapping slide never travels across the viewport.
      const outside = Math.min(lastVisible + 1, slides.length / 2);
      const outsideX = Math.max(width * (0.88 + (lastVisible - 1) * 0.5), viewport.clientWidth / 2 + width * 0.325 + 32);
      const tiers = [
        { x: 0, scale: 1, opacity: 1, brightness: 1, shadowY: 17, blur: 42, alpha: 0.2, border: 2 },
        { x: width * 0.5, scale: 0.8, opacity: 0.94, brightness: 0.92, shadowY: 9, blur: 24, alpha: 38 / 255, border: 2.5 },
        { x: width * 0.88, scale: 0.65, opacity: 0.78, brightness: 0.82, shadowY: 6, blur: 18, alpha: 31 / 255, border: 3.08 },
      ];
      const hidden = { ...tiers[lastVisible], x: outsideX, scale: 0.65, opacity: 0 };
      const activeIndex = logicalIndex();
      slides.forEach((slide, slideIndex) => {
        let relative = ((slideIndex - position) % slides.length + slides.length) % slides.length;
        if (relative > slides.length / 2) relative -= slides.length;
        const distance = Math.abs(relative);
        const tier = Math.min(lastVisible, Math.floor(distance));
        const from = tiers[tier];
        const to = tier === lastVisible ? hidden : tiers[tier + 1];
        const span = tier === lastVisible ? outside - lastVisible : 1;
        const fraction = Math.min(1, Math.max(0, (distance - tier) / span));
        const value = (property) => from[property] + (to[property] - from[property]) * fraction;
        slide.dataset.akibartCoverflowOffset = String(relative);
        slide.style.setProperty('--akibart-slide-offset-x', `${Math.sign(relative) * value('x')}px`);
        slide.style.setProperty('--akibart-slide-scale', String(value('scale')));
        slide.style.setProperty('--akibart-slide-brightness', String(value('brightness')));
        slide.style.setProperty('--akibart-slide-shadow', `0 0 0 ${value('border')}px #fff, 0 ${value('shadowY')}px ${value('blur')}px rgb(15 23 41 / ${value('alpha')})`);
        slide.style.zIndex = String(Math.round((slides.length / 2 - distance) * 1000) + 1);
        slide.style.opacity = String(value('opacity'));
        slide.style.pointerEvents = distance < outside ? '' : 'none';
        slide.removeAttribute('data-akibart-slide-jump');
        slide.toggleAttribute('aria-hidden', slideIndex !== activeIndex);
        if (slideIndex !== activeIndex) slide.setAttribute('aria-hidden', 'true');
      });
      slideTabIndexes.forEach((original, control) => {
        if (!slides[activeIndex].contains(control)) control.setAttribute('tabindex', '-1');
        else if (original === null) control.removeAttribute('tabindex');
        else control.setAttribute('tabindex', original);
      });
      if (thumbnailTrack && coverflowThumbnailPitch) {
        const phase = ((position % slides.length) + slides.length) % slides.length;
        let targetOffset = activeIndex - phase;
        if (targetOffset > slides.length / 2) targetOffset -= slides.length;
        else if (targetOffset < -slides.length / 2) targetOffset += slides.length;
        const visibleThumbnailIndex = thumbnailCloneCount + Math.round(phase + targetOffset);
        thumbnailTrack.style.transition = 'none';
        thumbnailTrack.style.transform = `translate3d(${coverflowThumbnailOrigin - phase * coverflowThumbnailPitch}px, 0, 0)`;
        if (thumbnailIndex !== visibleThumbnailIndex) {
          thumbnailIndex = visibleThumbnailIndex;
          updateControls();
        }
      }
    };
    const stopCoverflowMotion = () => {
      if (coverflowFrame !== null) window.cancelAnimationFrame(coverflowFrame);
      coverflowFrame = null;
      clearAnimationTimer();
      isAnimating = false;
    };
    // Solve the CSS cubic-bezier(0.16, 1, 0.3, 1) for elapsed time.
    const coverflowEase = (progress) => {
      if (progress <= 0) return 0;
      if (progress >= 1) return 1;
      let low = 0;
      let high = 1;
      for (let iteration = 0; iteration < 16; iteration += 1) {
        const t = (low + high) / 2;
        const x = 3 * (1 - t) ** 2 * t * 0.16 + 3 * (1 - t) * t ** 2 * 0.3 + t ** 3;
        if (x < progress) low = t;
        else high = t;
      }
      return 1 - (1 - (low + high) / 2) ** 3;
    };
    const animateCoverflowTo = (target) => {
      stopCoverflowMotion();
      const from = coverflowPosition;
      coverflowTarget = target;
      index = ((target % slides.length) + slides.length) % slides.length;
      mobileIndex = slides.length + index;
      updateControls();
      if (from === target) {
        coverflowPosition = coverflowTarget = logicalIndex();
        renderCoverflow(coverflowPosition);
        updateControls();
        updateHoveredSlide();
        scheduleAutoplay();
        return;
      }
      isAnimating = true;
      const started = window.performance.now();
      const duration = carouselDurationMs();
      const tick = (time) => {
        coverflowFrame = null;
        const progress = duration ? Math.min(1, Math.max(0, (time - started) / duration)) : 1;
        const eased = progress === 1 ? 1 : coverflowEase(progress);
        renderCoverflow(from + (target - from) * eased);
        if (progress < 1) coverflowFrame = window.requestAnimationFrame(tick);
        else {
          coverflowPosition = coverflowTarget = logicalIndex();
          finishMovement();
        }
      };
      coverflowFrame = window.requestAnimationFrame(tick);
    };
    const runCoverflowAction = (action) => {
      if (pointerState) {
        finishGesture(null, pointerState.inputType, true);
      }
      let target = coverflowTarget + Math.trunc(action.delta || 0);
      if (action.type === 'to') {
        const requested = ((Math.trunc(action.target) % slides.length) + slides.length) % slides.length;
        let delta = requested - ((coverflowPosition % slides.length) + slides.length) % slides.length;
        if (delta > slides.length / 2) delta -= slides.length;
        else if (delta < -slides.length / 2) delta += slides.length;
        target = Math.round(coverflowPosition + delta);
      }
      animateCoverflowTo(target);
    };

    const stripEditorAttributes = (element) => {
      [element, ...element.querySelectorAll('*')].forEach((node) => {
        [...node.attributes].forEach((attribute) => {
          if (attribute.name.startsWith('data-shopify-editor')) node.removeAttribute(attribute.name);
        });
      });
    };
    const gapFor = (element) => parseFloat(window.getComputedStyle(element).columnGap) || 0;
    const cloneThumbnail = (thumbnail) => {
      const clone = thumbnail.cloneNode(true);
      clone.dataset.akibartThumbnailClone = '';
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');
      clone.removeAttribute('aria-current');
      clone.querySelectorAll('img').forEach((image) => {
        image.loading = 'lazy';
        image.alt = '';
      });
      clone.querySelectorAll('a, button, [tabindex]').forEach((control) => control.setAttribute('tabindex', '-1'));
      stripEditorAttributes(clone);
      return clone;
    };

    const trackSlides = slides;

    let thumbnailTrackSlides = [];
    let thumbnailCloneCount = slides.length;
    let thumbnailIndex = 0;
    if (thumbnailTrack && thumbnailViewport && thumbnails.length === slides.length) {
      const firstThumbnailWidth = thumbnails[0].getBoundingClientRect().width;
      const thumbnailGap = gapFor(thumbnailTrack);
      const minimumCloneCount = Math.max(slides.length, 4, Math.ceil((thumbnailViewport.clientWidth / 2) / Math.max(1, firstThumbnailWidth + thumbnailGap)));
      thumbnailCloneCount = Math.ceil(minimumCloneCount / slides.length) * slides.length;
      thumbnailIndex = thumbnailCloneCount;
      const leadingThumbnails = document.createDocumentFragment();
      const trailingThumbnails = document.createDocumentFragment();
      for (let cloneIndex = 0; cloneIndex < thumbnailCloneCount; cloneIndex += 1) {
        leadingThumbnails.append(cloneThumbnail(thumbnails[cloneIndex % slides.length]));
        trailingThumbnails.append(cloneThumbnail(thumbnails[cloneIndex % slides.length]));
      }
      thumbnailTrack.prepend(leadingThumbnails);
      thumbnailTrack.append(trailingThumbnails);
      thumbnailTrackSlides = [...thumbnailTrack.children];
    }

    const logicalIndex = () => (index % slides.length + slides.length) % slides.length;
    if (slides.length > 1) {
      const leading = document.createDocumentFragment();
      const trailing = document.createDocumentFragment();
      slides.forEach((slide) => {
        [leading, trailing].forEach((fragment) => {
          const clone = slide.cloneNode(true);
          clone.removeAttribute('data-akibart-slide');
          clone.dataset.akibartSlideClone = '';
          clone.setAttribute('aria-hidden', 'true');
          clone.inert = true;
          clone.querySelectorAll('a, button, [tabindex]').forEach((control) => control.setAttribute('tabindex', '-1'));
          clone.querySelectorAll('img').forEach((image) => {
            image.loading = 'lazy';
            image.removeAttribute('fetchpriority');
          });
          stripEditorAttributes(clone);
          fragment.append(clone);
        });
      });
      track.prepend(leading);
      track.append(trailing);
    }
    const mobileTrackSlides = [...track.children];
    const mobileOffsetFor = () => {
      const slide = mobileTrackSlides[mobileIndex];
      if (!slide) return 0;
      const slideRect = slide.getBoundingClientRect();
      const trackRect = track.getBoundingClientRect();
      return viewport.clientWidth / 2 - (slideRect.left - trackRect.left + slideRect.width / 2);
    };
    const normalizeMobileIndex = () => {
      if (desktopMode.matches || slides.length < 2) return;
      if (mobileIndex < slides.length) mobileIndex += slides.length;
      else if (mobileIndex >= slides.length * 2) mobileIndex -= slides.length;
      else return;
      setPosition(false);
    };
    const fitDesktopWidth = () => {
      slider.style.removeProperty('width');
      slider.style.removeProperty('margin-left');
      if (!desktopMode.matches) return;
      slider.style.width = document.documentElement.clientWidth + 'px';
      slider.style.marginLeft = -slider.getBoundingClientRect().left + 'px';
    };
    const normalizedOffsetFor = (slideIndex) => {
      const count = slides.length;
      let offset = ((slideIndex - logicalIndex()) % count + count) % count;
      if (offset > count / 2) offset -= count;
      return offset;
    };
    const thumbnailOffsetFor = (thumbnailIndex) => {
      const thumbnail = thumbnailTrackSlides[thumbnailIndex];
      if (!thumbnail || !thumbnailViewport || !thumbnailTrack) return 0;
      const thumbnailWidth = thumbnail.getBoundingClientRect().width;
      const gap = gapFor(thumbnailTrack);
      const precedingWidth = thumbnailTrackSlides.slice(0, thumbnailIndex).reduce((total, item) => total + item.getBoundingClientRect().width, 0);
      return (thumbnailViewport.clientWidth - thumbnailWidth) / 2 - precedingWidth - thumbnailIndex * gap;
    };
    const setTransform = (element, offset, animate = true) => {
      if (!element) return;
      if (!animate) {
        element.style.transition = 'none';
        element.style.transform = 'translate3d(' + offset + 'px, 0, 0)';
        element.getBoundingClientRect();
        element.style.transition = '';
        return;
      }
      element.style.transition = '';
      element.style.transform = 'translate3d(' + offset + 'px, 0, 0)';
    };
    const slideTabIndexes = new Map();
    slides.forEach((slide) => {
      slide.querySelectorAll('a[href], button, [tabindex]').forEach((control) => {
        if (!slideTabIndexes.has(control)) slideTabIndexes.set(control, control.getAttribute('tabindex'));
      });
    });
    const setSlidePositions = (animate = true, movementDelta = 0) => {
      const continuousCoverflow = usesContinuousCoverflow();
      slider.classList.toggle('is-continuous-coverflow', continuousCoverflow);
      if (continuousCoverflow) {
        track.style.transform = '';
        track.style.setProperty('--akibart-slide-drag-offset', '0px');
        slider.classList.remove('is-jumping');
        pendingCoverflowExit = false;
        coverflowWidth = slides[0].offsetWidth || viewport.clientWidth;
        coverflowThumbnailPitch = thumbnailTrackSlides.length ? thumbnailTrackSlides[0].offsetWidth + gapFor(thumbnailTrack) : 0;
        coverflowThumbnailOrigin = thumbnailTrackSlides.length ? thumbnailOffsetFor(thumbnailCloneCount) : 0;
        coverflowPosition = coverflowTarget = logicalIndex();
        renderCoverflow(coverflowPosition);
        return;
      }
      slides.forEach((slide) => {
        slide.style.removeProperty('--akibart-slide-brightness');
        slide.style.removeProperty('--akibart-slide-shadow');
      });
      if (!desktopMode.matches) {
        slider.classList.toggle('is-jumping', !animate);
        slides.forEach((slide, slideIndex) => {
          ['--akibart-slide-offset-x', '--akibart-slide-scale', 'z-index', 'opacity', 'pointer-events'].forEach((property) => slide.style.removeProperty(property));
          slide.removeAttribute('data-akibart-slide-jump');
          if (slideIndex === logicalIndex()) slide.removeAttribute('aria-hidden');
          else slide.setAttribute('aria-hidden', 'true');
          slideTabIndexes.forEach((original, control) => {
            if (!slide.contains(control)) return;
            if (slideIndex !== logicalIndex()) control.setAttribute('tabindex', '-1');
            else if (original === null) control.removeAttribute('tabindex');
            else control.setAttribute('tabindex', original);
          });
        });
        mobileTrackSlides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slides.length === 1 || slideIndex === mobileIndex));
        if (slides.length > 1) setTransform(track, mobileOffsetFor(), animate);
        else track.style.transform = '';
        track.style.setProperty('--akibart-slide-drag-offset', '0px');
        return;
      }
      track.style.transform = '';
      const step = Number.parseFloat(window.getComputedStyle(slider).getPropertyValue('--akibart-coverflow-step')) || 0.3;
      const desktopCoverflow = desktopMode.matches && slider.classList.contains('akibart-home__hero--coverflow');
      const staticDesktopPair = desktopMode.matches && slider.classList.contains('akibart-home__hero--count-2');
      if (desktopCoverflow) slider.classList.toggle('is-jumping', !animate);
      pendingCoverflowExit = false;
      const desktopSlideWidth = trackSlides[0]?.offsetWidth || viewport.clientWidth;
      const mobileSlidePitch = desktopSlideWidth + gapFor(track);
      const maxCoverflowDistance = slides.length >= 5 ? 2 : 1;
      const activeIndex = logicalIndex();
      const positionDistanceFor = (distance) => desktopCoverflow
        ? desktopSlideWidth * (distance === 0 ? 0 : distance === 1 ? step : 0.88 + (distance - 2) * step)
        : staticDesktopPair ? 0 : distance * mobileSlidePitch;
      const positions = trackSlides.map((slide, slideIndex) => {
        const offset = normalizedOffsetFor(slideIndex);
        const distance = Math.abs(offset);
        const oldOffset = Number.parseInt(slide.dataset.akibartCoverflowOffset ?? `${offset}`, 10);
        const unnormalizedTargetOffset = oldOffset - movementDelta;
        const crossesLoopSeam = animate && desktopCoverflow && movementDelta !== 0
          && Number.isFinite(oldOffset) && unnormalizedTargetOffset !== offset;
        const oldDistance = Math.abs(oldOffset);
        return {
          slide,
          slideIndex,
          offset,
          distance,
          oldOffset,
          unnormalizedTargetOffset,
          incoming: crossesLoopSeam && distance <= maxCoverflowDistance,
          outgoing: crossesLoopSeam && oldDistance <= maxCoverflowDistance && distance > maxCoverflowDistance,
          legacySlideJump: animate && !desktopCoverflow && Number.isFinite(oldOffset) && Math.abs(oldOffset - offset) > 1,
        };
      });
      const incoming = positions.filter((position) => position.incoming);
      const dragOffset = Number.parseFloat(track.style.getPropertyValue('--akibart-slide-drag-offset')) || 0;
      incoming.forEach(({ slide, offset, unnormalizedTargetOffset }) => {
        const outsideDistance = maxCoverflowDistance + 1;
        const side = Math.sign(offset || unnormalizedTargetOffset) || 1;
        slide.dataset.akibartSlideJump = '';
        slide.style.setProperty('--akibart-slide-offset-x', `${side * positionDistanceFor(outsideDistance) - dragOffset}px`);
        slide.style.setProperty('--akibart-slide-scale', '0.65');
        slide.style.zIndex = '0';
        slide.style.opacity = '0';
        slide.style.pointerEvents = 'none';
      });
      if (incoming.length) incoming[0].slide.getBoundingClientRect();
      incoming.forEach(({ slide }) => slide.removeAttribute('data-akibart-slide-jump'));
      positions.forEach(({ slide, slideIndex, offset, distance, unnormalizedTargetOffset, outgoing, legacySlideJump }) => {
        const displayDistance = outgoing ? maxCoverflowDistance + 1 : distance;
        const displayOffset = outgoing
          ? (Math.sign(unnormalizedTargetOffset || offset) || 1) * displayDistance
          : offset;
        const positionDistance = positionDistanceFor(displayDistance);
        const isVisible = !desktopCoverflow || (!outgoing && distance <= maxCoverflowDistance);
        slide.dataset.akibartCoverflowOffset = String(offset);
        slide.style.setProperty('--akibart-slide-offset-x', `${Math.sign(displayOffset) * positionDistance}px`);
        slide.style.setProperty('--akibart-slide-scale', desktopCoverflow ? (displayDistance === 0 ? '1' : displayDistance === 1 ? '0.8' : '0.65') : '1');
        slide.style.zIndex = String(desktopCoverflow ? (distance === 0 ? 5 : distance === 1 ? 4 : distance === 2 ? 3 : 0) : (distance === 0 ? 2 : 1));
        slide.style.opacity = desktopCoverflow ? (isVisible ? (distance === 0 ? '1' : distance === 1 ? '0.94' : '0.78') : '0') : '1';
        slide.style.pointerEvents = isVisible ? '' : 'none';
        if (legacySlideJump) slide.dataset.akibartSlideJump = '';
        else slide.removeAttribute('data-akibart-slide-jump');
        if (outgoing) pendingCoverflowExit = true;
        if (staticDesktopPair || slideIndex === activeIndex) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
        slideTabIndexes.forEach((originalTabIndex, control) => {
          if (!slide.contains(control)) return;
          if (!staticDesktopPair && slideIndex !== activeIndex) control.setAttribute('tabindex', '-1');
          else if (originalTabIndex === null) control.removeAttribute('tabindex');
          else control.setAttribute('tabindex', originalTabIndex);
        });
      });
      track.style.setProperty('--akibart-slide-drag-offset', '0px');
      if (!desktopCoverflow) slider.classList.toggle('is-jumping', !animate);
    };
    const setPosition = (animate = true, movementDelta = 0) => {
      setSlidePositions(animate, movementDelta);
      if (thumbnailTrack) setTransform(thumbnailTrack, thumbnailOffsetFor(thumbnailIndex), animate);
      if (!animate) window.requestAnimationFrame(() => slider.classList.remove('is-jumping'));
    };
    const readTranslateX = (element, fallback) => {
      const transform = window.getComputedStyle(element).transform;
      if (transform === 'none') return fallback;
      try {
        return new DOMMatrixReadOnly(transform).m41;
      } catch {
        const values = transform.slice(transform.indexOf('(') + 1, transform.lastIndexOf(')')).split(',');
        return parseFloat(values.length === 16 ? values[12] : values[4]) || fallback;
      }
    };
    const updateControls = () => {
      const activeIndex = logicalIndex();
      dots.forEach((dot, dotIndex) => {
        if (dotIndex === activeIndex) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      thumbnailTrackSlides.forEach((thumbnail) => {
        const isCurrent = thumbnail === thumbnailTrackSlides[thumbnailIndex]
          && Number(thumbnail.dataset.akibartSlideTo) === activeIndex;
        thumbnail.classList.toggle('is-active', isCurrent);
        if (thumbnail.hasAttribute('data-akibart-thumbnail-clone')) {
          thumbnail.removeAttribute('aria-current');
        } else if (isCurrent) {
          thumbnail.setAttribute('aria-current', 'true');
        } else {
          thumbnail.removeAttribute('aria-current');
        }
      });
    };
    const normalizeThumbnailIndex = () => {
      if (!thumbnailTrackSlides.length) return false;
      if (thumbnailIndex < thumbnailCloneCount) thumbnailIndex += slides.length;
      else if (thumbnailIndex >= thumbnailCloneCount + slides.length) thumbnailIndex -= slides.length;
      else return false;
      setPosition(false);
      updateControls();
      return true;
    };
    const clearAutoplay = () => {
      if (autoplayTimer !== null) window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    };
    const clearAnimationTimer = () => {
      if (animationTimer !== null) window.clearTimeout(animationTimer);
      animationTimer = null;
    };
    const carouselDurationMs = () => {
      try {
        const value = window.getComputedStyle(slider).getPropertyValue('--akibart-carousel-duration').trim();
        const match = value.match(/^((?:\d+(?:\.\d*)?|\.\d+))(ms|s)$/i);
        if (!match) return 900;
        const duration = Number.parseFloat(match[1]);
        if (!Number.isFinite(duration) || duration < 0) return 900;
        return match[2].toLowerCase() === 's' ? duration * 1000 : duration;
      } catch {
        return 900;
      }
    };
    const autoplayBlocked = () => document.hidden || isHovered || isFocusVisible || dragging;
    const scheduleAutoplay = () => {
      clearAutoplay();
      if (!autoplayEnabled || !canUseCarousel() || autoplayBlocked() || isAnimating || queuedActions.length) return;
      autoplayTimer = window.setTimeout(() => {
        autoplayTimer = null;
        requestMoveBy(1);
      }, autoplayInterval);
    };
    const continueMovement = () => {
      const nextAction = queuedActions.shift();
      if (nextAction) runAction(nextAction);
      else scheduleAutoplay();
    };
    const finishMovement = () => {
      if (!isAnimating) return;
      isAnimating = false;
      clearAnimationTimer();
      if (pendingCoverflowExit) setPosition(false);
      normalizeMobileIndex();
      normalizeThumbnailIndex();
      updateControls();
      updateHoveredSlide();
      continueMovement();
    };
    const startAnimation = (movementDelta = 0) => {
      track.style.transition = '';
      if (thumbnailTrack) thumbnailTrack.style.transition = '';
      track.getBoundingClientRect();
      if (thumbnailTrack) thumbnailTrack.getBoundingClientRect();
      isAnimating = true;
      setPosition(true, movementDelta);
      animationTimer = window.setTimeout(finishMovement, carouselDurationMs() + 50);
    };
    const runAction = (action) => {
      normalizeThumbnailIndex();
      let delta = action.delta;
      if (action.type === 'to') {
        const target = ((Math.trunc(action.target) % slides.length) + slides.length) % slides.length;
        delta = target - logicalIndex();
        if (delta > slides.length / 2) delta -= slides.length;
        else if (delta < -slides.length / 2) delta += slides.length;
      }
      delta = Math.trunc(delta);
      delta = Math.max(-(slides.length - 1), Math.min(slides.length - 1, delta));
      if (!delta) {
        updateControls();
        continueMovement();
        return;
      }
      index = (index + delta + slides.length) % slides.length;
      mobileIndex += delta;
      thumbnailIndex += delta;
      updateControls();
      startAnimation(delta);
    };
    const requestMoveBy = (delta) => {
      clearAutoplay();
      if (!canUseCarousel()) return;
      const action = { type: 'by', delta };
      if (usesContinuousCoverflow()) {
        runCoverflowAction(action);
        return;
      }
      if (isAnimating) {
        queuedActions.push(action);
        return;
      }
      runAction(action);
    };
    const requestMoveTo = (target) => {
      clearAutoplay();
      if (!canUseCarousel()) return;
      const action = { type: 'to', target };
      if (usesContinuousCoverflow()) {
        runCoverflowAction(action);
        return;
      }
      if (isAnimating) {
        queuedActions.push(action);
        return;
      }
      runAction(action);
    };
    const resumeAutoplay = () => scheduleAutoplay();
    const pauseAutoplay = () => clearAutoplay();
    const updateHoveredSlide = (event = null) => {
      if (event) {
        if (event.pointerType !== 'mouse') return;
        mousePointerPosition = { x: event.clientX, y: event.clientY };
      }
      if (!mousePointerPosition) return;
      const element = document.elementFromPoint(mousePointerPosition.x, mousePointerPosition.y);
      const hoveredSlide = element?.closest('[data-akibart-slide]');
      const nextIsHovered = slider.contains(element) && hoveredSlide === slides[logicalIndex()];
      if (isHovered === nextIsHovered) return;
      isHovered = nextIsHovered;
      if (isHovered) pauseAutoplay();
      else resumeAutoplay();
    };
    const updateFocusVisible = (element) => {
      const nextIsFocusVisible = element instanceof Element
        && slider.contains(element)
        && element.matches(':focus-visible');
      if (isFocusVisible === nextIsFocusVisible) return;
      isFocusVisible = nextIsFocusVisible;
      if (isFocusVisible) pauseAutoplay();
      else resumeAutoplay();
    };

    const slidePitch = () => {
      if (window.matchMedia('(min-width: 750px)').matches) {
        const step = Number.parseFloat(window.getComputedStyle(slider).getPropertyValue('--akibart-coverflow-step')) || 0.5;
        return (trackSlides[0]?.offsetWidth || viewport.clientWidth) * step;
      }
      return (trackSlides[0]?.offsetWidth || viewport.clientWidth) + gapFor(track);
    };
    const thumbnailPitch = () => {
      const thumbnail = thumbnailTrackSlides[thumbnailIndex];
      return thumbnail ? (thumbnail.offsetWidth || thumbnail.getBoundingClientRect().width) + gapFor(thumbnailTrack) : slidePitch();
    };
    const gesturePoint = (event, inputType, identifier) => {
      if (inputType !== 'touch') return event;
      const touches = [...(event?.changedTouches || [])];
      return touches.find((touch) => touch.identifier === identifier) || null;
    };
    const addGestureSample = (state, point, event) => {
      const time = Number.isFinite(event?.timeStamp) ? event.timeStamp : window.performance.now();
      state.samples.push({ x: point.clientX, time });
      while (state.samples.length > 2 && state.samples[0].time < time - 160) state.samples.shift();
    };
    const startGesture = (event, surface, source, inputType) => {
      if (!canUseCarousel()) return;
      if (pointerState) return;
      if (inputType === 'pointer' && (!event.isPrimary || event.pointerType === 'touch' || (event.pointerType === 'mouse' && event.button !== 0))) return;
      if (inputType === 'touch' && event.touches.length !== 1) return;
      const point = inputType === 'touch' ? event.changedTouches[0] : event;
      if (!point) return;
      const continuousCoverflow = usesContinuousCoverflow();
        if (continuousCoverflow) {
        stopCoverflowMotion();
        coverflowWidth = slides[0].offsetWidth || viewport.clientWidth;
      } else if (isAnimating) {
        isAnimating = false;
        clearAnimationTimer();
        queuedActions.length = 0;
        setPosition(false);
      }
      const heroOffset = Number.parseFloat(track.style.getPropertyValue('--akibart-slide-drag-offset')) || 0;
      const currentThumbnailOffset = thumbnailTrack ? readTranslateX(thumbnailTrack, thumbnailOffsetFor(thumbnailIndex)) : 0;
      const time = Number.isFinite(event.timeStamp) ? event.timeStamp : window.performance.now();
      pointerState = {
        inputType,
        pointerId: inputType === 'touch' ? point.identifier : event.pointerId,
        surface,
        source,
        startX: point.clientX,
        startY: point.clientY,
        lastX: point.clientX,
        lastY: point.clientY,
        continuousCoverflow,
        coverflowPosition,
        dragStartX: point.clientX,
        heroPitch: continuousCoverflow ? coverflowWidth * 0.5 : 0,
        thumbnailPitch: continuousCoverflow ? coverflowThumbnailPitch || thumbnailPitch() : 0,
        heroOffset,
        thumbnailOffset: currentThumbnailOffset,
        axis: null,
        didDrag: false,
        samples: [{ x: point.clientX, time }],
      };
      slider.classList.add('is-pointer-active');
      track.style.setProperty('--akibart-slide-drag-offset', `${heroOffset}px`);
      if (!desktopMode.matches) track.style.transition = 'none';
      if (thumbnailTrack) {
        thumbnailTrack.style.transition = 'none';
        thumbnailTrack.style.transform = 'translate3d(' + currentThumbnailOffset + 'px, 0, 0)';
      }
      pauseAutoplay();
    };
    const startPointer = (event, surface, source) => startGesture(event, surface, source, 'pointer');
    const startTouch = (event, surface, source) => startGesture(event, surface, source, 'touch');
    const renderCoverflowGesture = (state) => {
      const deltaX = state.didDrag ? state.lastX - state.dragStartX : 0;
      const pitch = state.source === 'hero' ? state.heroPitch : state.thumbnailPitch;
      renderCoverflow(state.coverflowPosition - deltaX / pitch);
      slider.classList.toggle('is-dragging', state.didDrag);
    };
    const moveGesture = (event, inputType) => {
      const state = pointerState;
      if (!state || state.inputType !== inputType) return;
      const point = gesturePoint(event, inputType, state.pointerId);
      if (!point) return;
      state.lastX = point.clientX;
      state.lastY = point.clientY;
      addGestureSample(state, point, event);
      const deltaX = point.clientX - state.startX;
      const deltaY = point.clientY - state.startY;
      const threshold = state.continuousCoverflow && event.pointerType === 'mouse' ? 4 : 7;
      if (!state.axis && (Math.abs(deltaX) > threshold || Math.abs(deltaY) > threshold)) {
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          state.axis = 'horizontal';
          state.didDrag = true;
          dragging = true;
          if (state.continuousCoverflow) state.dragStartX = point.clientX;
          else slider.classList.add('is-dragging');
          if (inputType === 'pointer') {
            try {
              state.surface.setPointerCapture(event.pointerId);
            } catch {
              // Pointer capture can fail after the browser has cancelled the gesture.
            }
          }
        } else if (Math.abs(deltaY) > Math.abs(deltaX)) {
          state.axis = 'vertical';
        }
      }
      if (!state.didDrag) return;
      if (event.cancelable) event.preventDefault();
      if (state.continuousCoverflow) {
        if (coverflowFrame === null) {
          coverflowFrame = window.requestAnimationFrame(() => {
            coverflowFrame = null;
            if (pointerState === state) renderCoverflowGesture(state);
          });
        }
        return;
      }
      const heroDragDelta = state.source === 'hero' ? deltaX : deltaX * slidePitch() / thumbnailPitch();
      const thumbnailDragDelta = state.source === 'thumbnail' ? deltaX : deltaX * thumbnailPitch() / slidePitch();
      track.style.setProperty('--akibart-slide-drag-offset', `${state.heroOffset + heroDragDelta}px`);
      if (!desktopMode.matches) track.style.transform = 'translate3d(' + (mobileOffsetFor() + state.heroOffset + heroDragDelta) + 'px, 0, 0)';
      if (thumbnailTrack) {
        thumbnailTrack.style.transform = 'translate3d(' + (state.thumbnailOffset + thumbnailDragDelta) + 'px, 0, 0)';
      }
    };
    const pointerMove = (event) => moveGesture(event, 'pointer');
    const touchMove = (event) => moveGesture(event, 'touch');
    const swipeSteps = (state, deltaX) => {
      const samples = state.samples;
      const first = samples[0];
      const last = samples[samples.length - 1];
      const elapsed = Math.max(1, last.time - first.time);
      const velocity = (last.x - first.x) / elapsed;
      const speed = Math.abs(velocity);
      const pitch = state.source === 'thumbnail' ? thumbnailPitch() : slidePitch();
      if (Math.abs(deltaX) < pitch * 0.15 && speed < 0.65) return 0;
      return 1;
    };
    const snapToCurrentIndex = () => {
      const thumbnailTarget = thumbnailTrack ? thumbnailOffsetFor(thumbnailIndex) : 0;
      const heroCurrent = Number.parseFloat(track.style.getPropertyValue('--akibart-slide-drag-offset')) || 0;
      const thumbnailCurrent = thumbnailTrack ? readTranslateX(thumbnailTrack, thumbnailTarget) : thumbnailTarget;
      if (Math.abs(heroCurrent) < 1 && Math.abs(thumbnailTarget - thumbnailCurrent) < 1) {
        setPosition(false);
        updateControls();
        scheduleAutoplay();
        return;
      }
      startAnimation();
    };
    const finishGesture = (event, inputType, cancelled = false) => {
      if (!pointerState || pointerState.inputType !== inputType) return;
      const state = pointerState;
      if (inputType === 'pointer' && event && event.pointerId !== state.pointerId) return;
      const point = event ? gesturePoint(event, inputType, state.pointerId) : null;
      if (point) {
        state.lastX = point.clientX;
        state.lastY = point.clientY;
        addGestureSample(state, point, event);
      }
      if (state.continuousCoverflow) {
        stopCoverflowMotion();
        renderCoverflowGesture(state);
        const pitch = state.source === 'hero' ? state.heroPitch : state.thumbnailPitch;
        const deltaX = state.didDrag ? state.lastX - state.dragStartX : 0;
        const last = state.samples[state.samples.length - 1];
        const recent = state.samples.filter((sample) => sample.time >= last.time - 100);
        const first = recent[0];
        const velocity = (last.x - first.x) / Math.max(1, last.time - first.time) * state.heroPitch / pitch;
        const smallDrag = Math.abs(deltaX * state.heroPitch / pitch) < coverflowWidth * 0.15 && Math.abs(velocity) < 0.65;
        let target = Math.round(cancelled || smallDrag ? state.coverflowPosition : coverflowPosition);
        if (!cancelled && state.didDrag && Math.abs(velocity) >= 0.65) target -= Math.sign(velocity);
        pointerState = null;
        dragging = false;
        slider.classList.remove('is-dragging', 'is-pointer-active');
        if (state.didDrag) {
          ignoreLinkClickUntil = Date.now() + 400;
          if (state.source === 'thumbnail') ignoreThumbnailClickUntil = Date.now() + 400;
        }
        animateCoverflowTo(target);
        if (inputType === 'pointer' && event && state.surface.hasPointerCapture?.(event.pointerId)) state.surface.releasePointerCapture(event.pointerId);
        return;
      }
      const deltaX = state.lastX - state.startX;
      const deltaY = state.lastY - state.startY;
      if (!state.didDrag && !state.axis && Math.abs(deltaX) > 7 && Math.abs(deltaX) > Math.abs(deltaY)) state.didDrag = true;
      pointerState = null;
      dragging = false;
      slider.classList.remove('is-dragging');
      slider.classList.remove('is-pointer-active');
      if (state.didDrag) {
        ignoreLinkClickUntil = Date.now() + 400;
        if (state.source === 'thumbnail') ignoreThumbnailClickUntil = Date.now() + 400;
      }
      if (thumbnailTrack) thumbnailTrack.style.transition = '';
      if (state.didDrag) {
        slides.forEach((slide) => slide.getBoundingClientRect());
        if (thumbnailTrack) thumbnailTrack.getBoundingClientRect();
      }
      if (state.didDrag) {
        const steps = cancelled ? 0 : swipeSteps(state, deltaX);
        if (steps) {
          requestMoveBy(cancelled ? steps : deltaX < 0 ? steps : -steps);
        } else {
          snapToCurrentIndex();
        }
      } else {
        snapToCurrentIndex();
      }
      if (inputType === 'pointer' && event && state.surface.hasPointerCapture?.(event.pointerId)) state.surface.releasePointerCapture(event.pointerId);
    };
    const bindDragSurface = (surface, source) => {
      if (!surface) return;
      surface.addEventListener('pointerdown', (event) => startPointer(event, surface, source));
      surface.addEventListener('pointermove', pointerMove, { passive: false });
      surface.addEventListener('pointerup', (event) => finishGesture(event, 'pointer'));
      surface.addEventListener('pointercancel', (event) => finishGesture(event, 'pointer', true));
      surface.addEventListener('lostpointercapture', (event) => finishGesture(event, 'pointer', true));
      surface.addEventListener('touchstart', (event) => startTouch(event, surface, source), { passive: true });
      surface.addEventListener('touchmove', touchMove, { passive: false });
      surface.addEventListener('touchend', (event) => finishGesture(event, 'touch'), { passive: true });
      surface.addEventListener('touchcancel', (event) => finishGesture(event, 'touch', true), { passive: true });
    };

    setPosition(false);
    updateControls();
    slider.querySelector('[data-akibart-slider-previous]')?.addEventListener('click', () => requestMoveBy(-1));
    slider.querySelector('[data-akibart-slider-next]')?.addEventListener('click', () => requestMoveBy(1));

    slider.addEventListener('click', (event) => {
      if (Date.now() < ignoreLinkClickUntil && event.target.closest('a[href]')) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const control = event.target.closest('[data-akibart-slide-to]');
      if (!control || !slider.contains(control)) return;
      if (!canUseCarousel()) return;
      if (control.hasAttribute('data-akibart-thumbnail') && Date.now() < ignoreThumbnailClickUntil) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (control.tagName === 'A') {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
      }
      requestMoveTo(Number(control.dataset.akibartSlideTo));
    });

    track.addEventListener('click', (event) => {
      if (!canUseCarousel()) return;
      if (Date.now() < ignoreLinkClickUntil) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const slide = event.target.closest('[data-akibart-slide]');
      const slideIndex = slides.indexOf(slide);
      if (slideIndex < 0 || slideIndex === logicalIndex()) return;
      event.preventDefault();
      event.stopPropagation();
      requestMoveTo(slideIndex);
    }, true);
    slider.addEventListener('keydown', (event) => {
      if (!canUseCarousel()) return;
      if (event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        requestMoveBy(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        requestMoveBy(1);
      }
    });
    document.addEventListener('shopify:block:select', (event) => {
      const eventTarget = event.target instanceof Element ? event.target : null;
      const selectedSlide = eventTarget?.closest('[data-akibart-slide]');
      if (selectedSlide && slides.includes(selectedSlide)) {
        requestMoveTo(slides.indexOf(selectedSlide));
        return;
      }
      const blockId = event.detail?.blockId;
      if (!blockId) return;
      const matchingSlide = slides.find((slide) => {
        const blockData = slide.getAttribute('data-shopify-editor-block');
        if (!blockData) return false;
        try {
          return JSON.parse(blockData).id === blockId;
        } catch {
          return blockData.includes(blockId);
        }
      });
      if (matchingSlide) requestMoveTo(slides.indexOf(matchingSlide));
    });
    bindDragSurface(viewport, 'hero');
    bindDragSurface(thumbnailViewport, 'thumbnail');
    window.addEventListener('pointerup', (event) => finishGesture(event, 'pointer'));
    window.addEventListener('pointercancel', (event) => finishGesture(event, 'pointer', true));
    window.addEventListener('touchend', (event) => finishGesture(event, 'touch'), { passive: true });
    window.addEventListener('touchcancel', (event) => finishGesture(event, 'touch', true), { passive: true });
    window.addEventListener('blur', () => {
      if (pointerState) finishGesture(null, pointerState.inputType, true);
    });
    viewport.addEventListener('click', (event) => {
      if (Date.now() >= ignoreLinkClickUntil || !event.target.closest('a[href]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);

    slider.addEventListener('pointerover', updateHoveredSlide);
    slider.addEventListener('pointerout', updateHoveredSlide);
    slider.addEventListener('pointermove', updateHoveredSlide);
    slider.addEventListener('focusin', (event) => updateFocusVisible(event.target));
    slider.addEventListener('focusout', (event) => {
      updateFocusVisible(event.relatedTarget);
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) pauseAutoplay();
      else resumeAutoplay();
    });
    window.addEventListener('resize', () => {
      fitDesktopWidth();
      if (coverflowFrame !== null) stopCoverflowMotion();
      if (isAnimating) {
        isAnimating = false;
        clearAnimationTimer();
      }
      setPosition(false);
      normalizeThumbnailIndex();
      updateControls();
      if (queuedActions.length) continueMovement();
      else scheduleAutoplay();
    }, { passive: true });

    const repositionForModeChange = () => {
      clearAutoplay();
      stopCoverflowMotion();
      if (isAnimating) {
        isAnimating = false;
        clearAnimationTimer();
      }
      queuedActions.length = 0;
      pointerState = null;
      dragging = false;
      isHovered = false;
      mobileIndex = slides.length + logicalIndex();
      slider.classList.remove('is-dragging', 'is-pointer-active');
      fitDesktopWidth();
      setPosition(false);
      updateControls();
      scheduleAutoplay();
    };
    if (desktopMode.addEventListener) desktopMode.addEventListener('change', repositionForModeChange);
    else desktopMode.addListener(repositionForModeChange);

    fitDesktopWidth();
    setPosition(false);
    scheduleAutoplay();
  };
  const initializeGallery = (gallery) => {
    const main = gallery.querySelector('.akibart-product-gallery__main');
    const mainImage = gallery.querySelector('[data-akibart-main-image]');
    const controls = [...gallery.querySelectorAll('.akibart-product-gallery__thumbnails [data-akibart-gallery-image]')];
    const lightbox = gallery.querySelector('[data-akibart-lightbox]');
    const lightboxImage = lightbox?.querySelector('[data-akibart-lightbox-image]');
    const closeButton = lightbox?.querySelector('[data-akibart-lightbox-close]');
    const previousButton = lightbox?.querySelector('[data-akibart-lightbox-previous]');
    const nextButton = lightbox?.querySelector('[data-akibart-lightbox-next]');
    const lightboxCounter = lightbox?.querySelector('[data-akibart-lightbox-counter]');
    if (!main || !mainImage) return;

    let lightboxOpen = false;
    let lightboxCloseTimer = null;
    let hadScrollLockBeforeLightbox = false;
    let lightboxOwnsScrollLock = false;
    let ignoreMainClickUntil = 0;
    let galleryPointer = null;

    const currentImageIndex = () => Math.max(0, controls.findIndex((button) => button.getAttribute('aria-current') === 'true'));
    const updateLightboxCounter = () => {
      if (lightboxCounter && controls.length > 1) lightboxCounter.textContent = `${currentImageIndex() + 1} / ${controls.length}`;
    };
    const moveImage = (direction) => {
      if (controls.length < 2) return;
      const nextIndex = (currentImageIndex() + direction + controls.length) % controls.length;
      controls[nextIndex].click();
      updateLightboxCounter();
    };

    const openLightbox = () => {
      if (!lightbox || !lightboxImage || !closeButton || lightboxOpen) return;
      if (lightboxCloseTimer) window.clearTimeout(lightboxCloseTimer);
      if (!lightboxOwnsScrollLock) {
        hadScrollLockBeforeLightbox = document.documentElement.classList.contains('akibart-lock-scroll');
        lightboxOwnsScrollLock = true;
      }
      lightboxImage.src = mainImage.dataset.akibartMainImageFull || mainImage.currentSrc || mainImage.src;
      lightboxImage.alt = mainImage.alt;
      updateLightboxCounter();
      lightbox.hidden = false;
      lightbox.setAttribute('aria-hidden', 'false');
      lightboxOpen = true;
      document.documentElement.classList.add('akibart-lock-scroll');
      window.requestAnimationFrame(() => {
        if (!lightboxOpen) return;
        lightbox.classList.add('is-open');
        window.requestAnimationFrame(() => {
          if (lightboxOpen) closeButton.focus({ preventScroll: true });
        });
      });
    };

    const closeLightbox = () => {
      if (!lightbox || !lightboxOpen) return;
      lightboxOpen = false;
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      lightboxCloseTimer = window.setTimeout(() => {
        if (lightboxOpen) return;
        lightbox.hidden = true;
        lightboxImage?.removeAttribute('src');
        if (!hadScrollLockBeforeLightbox) document.documentElement.classList.remove('akibart-lock-scroll');
        lightboxOwnsScrollLock = false;
        mainImage.focus({ preventScroll: true });
      }, 250);
    };

    if (lightbox && lightboxImage && closeButton) {
      mainImage.addEventListener('click', () => {
        if (Date.now() >= ignoreMainClickUntil) openLightbox();
      });
      mainImage.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        openLightbox();
      });
      closeButton.addEventListener('click', closeLightbox);
      previousButton?.addEventListener('click', () => moveImage(-1));
      nextButton?.addEventListener('click', () => moveImage(1));
      lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox) closeLightbox();
      });
      lightboxImage.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch') return;
        galleryPointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
        try {
          lightboxImage.setPointerCapture(event.pointerId);
        } catch {
          // The swipe still works when pointer capture is unavailable.
        }
      });
      lightboxImage.addEventListener('pointerup', (event) => {
        if (!galleryPointer || event.pointerId !== galleryPointer.id) return;
        const deltaX = event.clientX - galleryPointer.x;
        const deltaY = event.clientY - galleryPointer.y;
        galleryPointer = null;
        if (deltaY > 60 && deltaY > Math.abs(deltaX)) closeLightbox();
        else if (Math.abs(deltaX) > 45 && Math.abs(deltaX) > Math.abs(deltaY)) moveImage(deltaX < 0 ? 1 : -1);
      });
      lightboxImage.addEventListener('pointercancel', () => { galleryPointer = null; });
      document.addEventListener('keydown', (event) => {
        if (!lightboxOpen) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          closeLightbox();
        } else if (event.key === 'ArrowLeft' && controls.length > 1) {
          event.preventDefault();
          moveImage(-1);
        } else if (event.key === 'ArrowRight' && controls.length > 1) {
          event.preventDefault();
          moveImage(1);
        } else if (event.key === 'Tab') {
          const focusableControls = [closeButton, previousButton, nextButton].filter((control) => control && !control.disabled && !control.hidden);
          if (focusableControls.length === 0) return;
          const currentIndex = focusableControls.indexOf(document.activeElement);
          event.preventDefault();
          const nextIndex = event.shiftKey
            ? (currentIndex <= 0 ? focusableControls.length - 1 : currentIndex - 1)
            : (currentIndex < 0 || currentIndex === focusableControls.length - 1 ? 0 : currentIndex + 1);
          focusableControls[nextIndex].focus({ preventScroll: true });
        }
      });
    }

    if (controls.length < 2) return;
    main.addEventListener('pointerdown', (event) => {
      galleryPointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
    }, { passive: true });
    main.addEventListener('pointerup', (event) => {
      if (!galleryPointer || event.pointerId !== galleryPointer.id) return;
      const deltaX = event.clientX - galleryPointer.x;
      const deltaY = event.clientY - galleryPointer.y;
      galleryPointer = null;
      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      ignoreMainClickUntil = Date.now() + 400;
      const currentIndex = currentImageIndex();
      const direction = deltaX < 0 ? 1 : -1;
      controls[(currentIndex + direction + controls.length) % controls.length].click();
    }, { passive: true });
    main.addEventListener('pointercancel', () => { galleryPointer = null; }, { passive: true });
  };

  const initializeCarousel = (carousel) => {
    const viewport = carousel.querySelector('[data-akibart-carousel-viewport]');
    const track = carousel.querySelector('[data-akibart-carousel-track]');
    const previous = carousel.querySelector('[data-akibart-carousel-previous]');
    const next = carousel.querySelector('[data-akibart-carousel-next]');
    if (!viewport || !track || !previous || !next) return;

    const cards = () => [...track.querySelectorAll(':scope > .akibart-product-card, :scope > .akibart-work-card')];
    const initialCards = cards();
    if (initialCards.length === 0) {
      previous.disabled = true;
      next.disabled = true;
      return;
    }
    track.querySelectorAll('a[href], img').forEach((element) => { element.draggable = false; });
    track.addEventListener('dragstart', (event) => event.preventDefault());
    const gapSize = () => Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
    const visibleItems = () => Number.parseFloat(window.getComputedStyle(carousel).getPropertyValue('--akibart-carousel-items-visible')) || 5;
    const cardPitch = () => {
      const firstCard = cards()[0];
      return firstCard ? firstCard.getBoundingClientRect().width + gapSize() : 1;
    };
    let offset = 0;
    let pointer = null;
    let ignoreClickUntil = 0;

    const maxOffset = () => Math.max(0, track.scrollWidth - viewport.clientWidth);
    const updateControls = () => {
      const maximum = maxOffset();
      previous.disabled = maximum <= 1 || offset <= 1;
      next.disabled = maximum <= 1 || offset >= maximum - 1;
    };
    const setOffset = (requestedOffset, animate = true) => {
      offset = Math.max(0, Math.min(maxOffset(), requestedOffset));
      if (!animate) track.style.transition = 'none';
      else track.style.transition = '';
      track.style.transform = `translate3d(${-offset}px, 0, 0)`;
      updateControls();
      if (!animate) {
        window.requestAnimationFrame(() => {
          if (!pointer) track.style.transition = '';
        });
      }
    };
    const measure = () => {
      const previousPitch = cardPitch();
      const previousIndex = previousPitch > 1 ? Math.round(offset / previousPitch) : 0;
      const gap = gapSize();
      const width = Math.max(0, (viewport.clientWidth - gap * (visibleItems() - 1)) / visibleItems());
      track.style.setProperty('--akibart-carousel-card-width', `${width}px`);
      setOffset(previousIndex * cardPitch(), false);
    };
    const move = (direction) => setOffset(offset + cardPitch() * direction);

    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));

    const pointForGesture = (event, inputType, identifier) => {
      if (inputType !== 'touch') return event;
      return [...(event?.changedTouches || [])].find((touch) => touch.identifier === identifier) || null;
    };
    const addGestureSample = (gesture, point, event) => {
      const time = Number.isFinite(event?.timeStamp) ? event.timeStamp : window.performance.now();
      gesture.samples.push({ x: point.clientX, time });
      while (gesture.samples.length > 2 && gesture.samples[0].time < time - 160) gesture.samples.shift();
    };
    const startGesture = (event, inputType) => {
      if (pointer) return;
      if (inputType === 'pointer' && (!event.isPrimary || event.pointerType === 'touch' || (event.pointerType === 'mouse' && event.button !== 0))) return;
      if (inputType === 'touch' && event.touches.length !== 1) return;
      if (event.target instanceof Element && event.target.closest('input, select, textarea, [contenteditable="true"]')) return;
      const point = inputType === 'touch' ? event.changedTouches[0] : event;
      if (!point) return;
      const transform = window.getComputedStyle(track).transform;
      const values = transform && transform !== 'none'
        ? transform.slice(transform.indexOf('(') + 1, -1).split(',').map((value) => Number.parseFloat(value))
        : [];
      const renderedX = transform.startsWith('matrix3d') ? values[12] : values[4];
      const renderedOffset = Number.isFinite(renderedX) ? -renderedX : offset;
      const time = Number.isFinite(event.timeStamp) ? event.timeStamp : window.performance.now();
      pointer = {
        inputType,
        id: inputType === 'touch' ? point.identifier : event.pointerId,
        startX: point.clientX,
        startY: point.clientY,
        startOffset: Math.max(0, Math.min(maxOffset(), renderedOffset)),
        didDrag: false,
        axis: null,
        samples: [{ x: point.clientX, time }],
      };
      carousel.classList.add('is-pointer-active');
      setOffset(pointer.startOffset, false);
    };
    const startPointer = (event) => startGesture(event, 'pointer');
    const startTouch = (event) => startGesture(event, 'touch');

    const moveGesture = (event, inputType) => {
      if (!pointer || pointer.inputType !== inputType) return;
      if (inputType === 'pointer' && event.pointerId !== pointer.id) return;
      const point = pointForGesture(event, inputType, pointer.id);
      if (!point) return;
      addGestureSample(pointer, point, event);
      const deltaX = point.clientX - pointer.startX;
      const deltaY = point.clientY - pointer.startY;
      if (!pointer.axis && (Math.abs(deltaX) > 7 || Math.abs(deltaY) > 7)) {
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          pointer.axis = 'horizontal';
          pointer.didDrag = true;
          carousel.classList.add('is-dragging');
          if (inputType === 'pointer') {
            try {
              // Capture only after horizontal intent is clear, so ordinary taps still click their link/button.
              viewport.setPointerCapture(event.pointerId);
            } catch {
              // Continue tracking when pointer capture is unavailable.
            }
          }
        } else if (Math.abs(deltaY) > Math.abs(deltaX)) {
          pointer.axis = 'vertical';
        }
      }
      if (!pointer.didDrag) return;
      if (event.cancelable) event.preventDefault();
      setOffset(pointer.startOffset + pointer.startX - point.clientX, false);
    };
    const pointerMove = (event) => moveGesture(event, 'pointer');
    const touchMove = (event) => moveGesture(event, 'touch');

    const finishGesture = (event, inputType, cancelled = false) => {
      if (!pointer || pointer.inputType !== inputType) return;
      if (inputType === 'pointer' && event && event.pointerId !== pointer.id) return;
      const activePointer = pointer;
      const point = event ? pointForGesture(event, inputType, activePointer.id) : null;
      if (point && activePointer.didDrag) {
        setOffset(activePointer.startOffset + activePointer.startX - point.clientX, false);
        addGestureSample(activePointer, point, event);
      }
      pointer = null;
      carousel.classList.remove('is-dragging');
      carousel.classList.remove('is-pointer-active');

      if (activePointer.didDrag) ignoreClickUntil = Date.now() + 400;
      const pitch = cardPitch();
      let projectedOffset = offset;
      if (activePointer.didDrag && !cancelled && pitch > 1 && activePointer.samples.length > 1) {
        const firstSample = activePointer.samples[0];
        const lastSample = activePointer.samples[activePointer.samples.length - 1];
        const elapsed = Math.max(1, lastSample.time - firstSample.time);
        const velocity = (lastSample.x - firstSample.x) / elapsed;
        const deltaX = lastSample.x - activePointer.startX;
        if (Math.abs(deltaX) >= Math.max(40, pitch * 0.08) || Math.abs(velocity) >= 0.65) {
          projectedOffset -= Math.sign(velocity) * Math.min(Math.abs(velocity) * 450, pitch * 3);
        }
      }
      const destination = pitch > 1 ? Math.round(projectedOffset / pitch) * pitch : projectedOffset;
      setOffset(destination);
      if (inputType === 'pointer' && event && viewport.hasPointerCapture?.(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
    };
    viewport.addEventListener('pointerdown', startPointer);
    viewport.addEventListener('pointermove', pointerMove, { passive: false });
    viewport.addEventListener('pointerup', (event) => finishGesture(event, 'pointer'));
    viewport.addEventListener('pointercancel', (event) => finishGesture(event, 'pointer', true));
    viewport.addEventListener('lostpointercapture', (event) => finishGesture(event, 'pointer', true));
    viewport.addEventListener('touchstart', startTouch, { passive: true });
    viewport.addEventListener('touchmove', touchMove, { passive: false });
    viewport.addEventListener('touchend', (event) => finishGesture(event, 'touch'), { passive: true });
    viewport.addEventListener('touchcancel', (event) => finishGesture(event, 'touch', true), { passive: true });
    window.addEventListener('pointerup', (event) => finishGesture(event, 'pointer'));
    window.addEventListener('pointercancel', (event) => finishGesture(event, 'pointer', true));
    window.addEventListener('touchend', (event) => finishGesture(event, 'touch'), { passive: true });
    window.addEventListener('touchcancel', (event) => finishGesture(event, 'touch', true), { passive: true });
    document.addEventListener('click', (event) => {
      if (
        !(event.target instanceof Node) ||
        !carousel.contains(event.target) ||
        (event.target instanceof Element && event.target.closest('[data-akibart-carousel-previous], [data-akibart-carousel-next]')) ||
        Date.now() >= ignoreClickUntil ||
        event.detail === 0
      ) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);

    if ('ResizeObserver' in window) {
      new ResizeObserver(measure).observe(viewport);
    } else {
      window.addEventListener('resize', measure, { passive: true });
    }
    measure();
  };

  const akibartButtonResetTimers = new WeakMap();
  let akibartCartToast = null;
  let akibartCartToastTimer = null;
  let akibartCartToastFrame = null;
  let akibartCartRefreshSequence = 0;
  let akibartCartMutationVersion = 0;
  let akibartPendingCartMutations = 0;
  let akibartCartMutationQueue = Promise.resolve();
  let akibartCartRefreshTimer = null;
  let akibartCartSnapshot = null;
  let akibartCartSnapshotPromise = null;
  let akibartCartSnapshotRevision = 0;
  let akibartLatestCartSections = null;
  let akibartDisplayedCartCount = null;
  let akibartQuantityFlushTimer = null;
  let akibartPendingQuantityRequests = 0;
  let akibartQuantityRevision = 0;
  let akibartCloseDrawerOnEmpty = false;
  let akibartAddFlushTimer = null;
  const pendingAkibartAddBatches = new Map();
  const pendingAkibartQuantityChanges = new Map();
  const latestAkibartQuantityRevisions = new Map();

  const clearAkibartFormError = (formComponent) => {
    const error = formComponent.querySelector('.product-form-text__error');
    if (!error) return;
    error.textContent = '';
    error.classList.add('hidden');
  };

  const showAkibartFormError = (formComponent, button, message) => {
    button?.removeAttribute('data-akibart-added');
    button?.setAttribute('data-akibart-error', 'true');
    const error = formComponent.querySelector('.product-form-text__error');
    if (error) {
      error.textContent = message;
      error.classList.remove('hidden');
    }
  };

  const showAkibartCartToast = (message, type = 'success') => {
    if (!akibartCartToast) {
      akibartCartToast = document.createElement('div');
      akibartCartToast.className = 'akibart-cart-toast';
      akibartCartToast.setAttribute('role', 'status');
      akibartCartToast.setAttribute('aria-live', 'polite');
      akibartCartToast.setAttribute('aria-atomic', 'true');
      document.body.append(akibartCartToast);
    }
    akibartCartToast.classList.toggle('is-error', type === 'error');
    akibartCartToast.textContent = message;
    if (akibartCartToastFrame !== null) window.cancelAnimationFrame(akibartCartToastFrame);
    akibartCartToastFrame = window.requestAnimationFrame(() => {
      akibartCartToastFrame = null;
      akibartCartToast?.classList.add('is-visible');
    });
    if (akibartCartToastTimer !== null) window.clearTimeout(akibartCartToastTimer);
    akibartCartToastTimer = window.setTimeout(() => {
      if (akibartCartToastFrame !== null) {
        window.cancelAnimationFrame(akibartCartToastFrame);
        akibartCartToastFrame = null;
      }
      akibartCartToast?.classList.remove('is-visible');
      akibartCartToastTimer = null;
    }, 1800);
  };

  const getAkibartProductImage = (formComponent) => {
    const cardImage = formComponent.closest('.akibart-product-card')?.querySelector('.akibart-product-card__primary-image');
    const productImage = formComponent.closest('.akibart-product')?.querySelector('[data-akibart-main-image]');
    const image = cardImage || productImage;
    return image?.currentSrc || image?.src || '';
  };

  const animateAkibartButtonPress = (button) => {
    if (!button || typeof button.animate !== 'function') return;
    const press = button.animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(0.96)', offset: 0.35 },
        { transform: 'scale(1)' },
      ],
      { duration: 170, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' }
    );
    press.finished.catch(() => {});
  };

  const animateAkibartFlyToCart = (formComponent, button) => {
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const imageUrl = getAkibartProductImage(formComponent);
    if (!cartIcon || !button || !imageUrl) return;
    const flyer = document.createElement('fly-to-cart');
    flyer.className = 'akibart-fly-to-cart fly-to-cart--quick';
    flyer.setAttribute('aria-hidden', 'true');
    flyer.style.backgroundImage = `url("${imageUrl.replace(/["\\\n\r]/g, '\\$&')}")`;
    flyer.style.setProperty('--start-opacity', '0');
    flyer.source = button;
    flyer.destination = cartIcon;
    document.body.append(flyer);
  };

  const animateAkibartCartIcon = () => {
    document.querySelectorAll('.header-actions__cart-icon').forEach((cartIcon) => {
      cartIcon.classList.remove('akibart-cart-bounce');
      void cartIcon.offsetWidth;
      cartIcon.classList.add('akibart-cart-bounce');
      window.setTimeout(() => cartIcon.classList.remove('akibart-cart-bounce'), 570);
    });
  };

  const getAkibartCartUrl = (path) => `${window.Shopify?.routes?.root || '/'}${path}`;

  const renderAkibartCartCount = (count) => {
    akibartDisplayedCartCount = count;
    document.querySelectorAll('cart-icon.header-actions__cart-icon').forEach((cartIcon) => {
      if (typeof cartIcon.renderCartBubble === 'function') {
        void cartIcon.renderCartBubble(count, false);
        return;
      }
      const bubble = cartIcon.querySelector('.cart-bubble');
      const countElement = cartIcon.querySelector('.cart-bubble__text-count');
      if (!bubble || !countElement) return;
      countElement.textContent = count < 100 ? String(count) : '';
      countElement.classList.toggle('hidden', count === 0);
      bubble.classList.toggle('visually-hidden', count === 0);
      cartIcon.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
    });
    document.querySelectorAll('header-actions [data-testid="cart-count-live-region"]').forEach((region) => {
      region.textContent = `${window.Theme?.translations?.cart_count || 'カートの商品数'}: ${count}`;
    });
  };

  const fetchAkibartCart = async () => {
    const response = await fetch(getAkibartCartUrl('cart.js'), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`カートを更新できませんでした (${response.status})`);
    return response.json();
  };

  const getAkibartCartSnapshot = () => {
    if (akibartCartSnapshot) return Promise.resolve(akibartCartSnapshot);
    if (akibartCartSnapshotPromise) return akibartCartSnapshotPromise;

    const revision = ++akibartCartSnapshotRevision;
    const request = fetchAkibartCart()
      .then((cart) => {
        if (akibartCartSnapshotRevision === revision) akibartCartSnapshot = cart;
        return cart;
      })
      .finally(() => {
        if (akibartCartSnapshotRevision === revision) akibartCartSnapshotPromise = null;
      });
    akibartCartSnapshotPromise = request;
    return request;
  };

  const invalidateAkibartCartSnapshot = () => {
    akibartCartSnapshotRevision += 1;
    akibartCartSnapshot = null;
    akibartCartSnapshotPromise = null;
  };

  const getAkibartCartCount = (cart) => {
    const count = Number(cart?.item_count ?? cart?.total_quantity ?? cart?.totalQuantity);
    if (Number.isFinite(count)) return count;
    return Array.isArray(cart?.items) ? cart.items.reduce((total, item) => total + Number(item.quantity || 0), 0) : 0;
  };

  const getAkibartCurrencyPrecision = (currency) => {
    try {
      return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits;
    } catch {
      return 2;
    }
  };

  const formatAkibartCents = (moneyValue, format, currency) => {
    const centsPerUnit = 100;
    if (!format) {
      const precision = getAkibartCurrencyPrecision(currency);
      return new Intl.NumberFormat(document.documentElement.lang || 'ja-JP', {
        style: 'currency',
        currency,
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }).format(Number(moneyValue || 0) / centsPerUnit);
    }

    const currencyPrecision = getAkibartCurrencyPrecision(currency);
    return format.replace(/{{\s*(\w+)\s*}}/g, (match, placeholder) => {
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = currencyPrecision;
      switch (placeholder) {
        case 'amount_no_decimals':
          precision = 0;
          break;
        case 'amount_with_comma_separator':
          thousandsSeparator = '.';
          decimalSeparator = ',';
          break;
        case 'amount_no_decimals_with_comma_separator':
          thousandsSeparator = '.';
          precision = 0;
          break;
        case 'amount_no_decimals_with_space_separator':
          thousandsSeparator = ' ';
          precision = 0;
          break;
        case 'amount_with_space_separator':
          thousandsSeparator = ' ';
          decimalSeparator = ',';
          break;
        case 'amount_with_period_and_space_separator':
          thousandsSeparator = ' ';
          decimalSeparator = '.';
          break;
        case 'amount_with_apostrophe_separator':
          thousandsSeparator = "'";
          decimalSeparator = '.';
          break;
        default:
          break;
      }

      const rounded = (Number(moneyValue || 0) / centsPerUnit).toFixed(precision);
      let [integer, fraction = ''] = rounded.split('.');
      integer = integer.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);
      return precision > 0 ? `${integer}${decimalSeparator}${fraction.padEnd(precision, '0')}` : integer;
    });
  };

  const setAkibartTextComponentValue = (element, value) => {
    if (!element) return;
    element.setAttribute('value', value);
    const bdi = element.querySelector('bdi');
    if (bdi) bdi.textContent = value;
  };

  const formatAkibartCartAmount = (component, amount, useCurrencyCode) => {
    const currency = component.dataset.currency || window.Shopify?.currency?.active || 'JPY';
    const sourceFormat = useCurrencyCode
      ? component.dataset.cartMoneyWithCurrencyFormat
      : component.dataset.cartMoneyFormat;
    const decoder = document.createElement('textarea');
    decoder.innerHTML = sourceFormat || '';
    const format = decoder.value;
    return formatAkibartCents(amount, format, currency);
  };

  const renderAkibartCartTotal = (component, amount) => {
    const drawer = component.closest('theme-drawer#cart-drawer') || component.closest('cart-drawer-component');
    const scope = drawer || component;
    const useCurrencyCode = component.dataset.cartTotalMoneyWithCurrency === 'true';
    const formatted = formatAkibartCartAmount(component, amount, useCurrencyCode);
    scope.querySelectorAll('[data-cart-subtotal]').forEach((element) => setAkibartTextComponentValue(element, formatted));
  };

  const renderAkibartCartLineTotal = (component, row, amount) => {
    const useCurrencyCode = component.dataset.cartItemsMoneyWithCurrency === 'true';
    const formatted = formatAkibartCartAmount(component, amount, useCurrencyCode);
    setAkibartTextComponentValue(row.querySelector('.cart-items__price text-component'), formatted);
  };

  const readAkibartDisplayedCartCount = (component) => {
    if (Number.isFinite(akibartDisplayedCartCount)) return akibartDisplayedCartCount;
    const icon = document.querySelector('cart-icon.header-actions__cart-icon');
    const iconCount = Number(icon?.currentCartCount);
    if (Number.isFinite(iconCount)) return iconCount;
    const hiddenCount = Number(component.querySelector('[ref="cartItemCount"]')?.textContent);
    return Number.isFinite(hiddenCount) ? hiddenCount : 0;
  };

  const updateAkibartOptimisticQuantity = (component, row, input, quantity) => {
    const oldQuantity = Number(row.dataset.lineQuantity ?? input.defaultValue ?? 0);
    const oldLineTotal = Number(row.dataset.finalLinePrice);
    if (!Number.isFinite(oldLineTotal) || oldQuantity === quantity) return oldQuantity;

    const cartItem = akibartCartSnapshot?.items?.find((item) => item.key === row.dataset.key);
    const unitPrice = oldQuantity > 0
      ? oldLineTotal / oldQuantity
      : Number(cartItem?.final_price || cartItem?.price || 0);
    const nextLineTotal = Math.max(0, Math.round(unitPrice * quantity));
    const currentTotal = Number(component.dataset.cartTotalPrice || 0);
    const nextTotal = Math.max(0, currentTotal + nextLineTotal - oldLineTotal);
    const currentCount = readAkibartDisplayedCartCount(component);

    row.dataset.lineQuantity = String(quantity);
    row.dataset.finalLinePrice = String(nextLineTotal);
    component.dataset.cartTotalPrice = String(nextTotal);
    renderAkibartCartLineTotal(component, row, nextLineTotal);
    renderAkibartCartTotal(component, nextTotal);
    renderAkibartCartCount(Math.max(0, currentCount + quantity - oldQuantity));
    return oldQuantity;
  };

  const restoreAkibartQuantityFromSnapshot = (component, key) => {
    const item = akibartCartSnapshot?.items?.find((candidate) => candidate.key === key);
    const row = [...component.querySelectorAll('tr[data-key]')].find((candidate) => candidate.dataset.key === key);
    const input = row?.querySelector('input[data-cart-line]');
    if (!item || !row || !input) return;

    const displayedQuantity = Number(row.dataset.lineQuantity || 0);
    const displayedLineTotal = Number(row.dataset.finalLinePrice || 0);
    const actualLineTotal = Number(item.final_line_price || 0);
    const currentTotal = Number(component.dataset.cartTotalPrice || 0);
    const currentCount = readAkibartDisplayedCartCount(component);
    row.dataset.lineQuantity = String(item.quantity);
    row.dataset.finalLinePrice = String(actualLineTotal);
    component.dataset.cartTotalPrice = String(Math.max(0, currentTotal + actualLineTotal - displayedLineTotal));
    input.value = String(item.quantity);
    row.querySelector('cart-quantity-selector-component')?.updateButtonStates?.();
    renderAkibartCartLineTotal(component, row, actualLineTotal);
    renderAkibartCartTotal(component, Number(component.dataset.cartTotalPrice));
    renderAkibartCartCount(Math.max(0, currentCount + Number(item.quantity) - displayedQuantity));
  };

  const getAkibartCartSectionIds = () => [...new Set(
    [...document.querySelectorAll('cart-items-component')]
      .map((component) => component.dataset.sectionId)
      .filter(Boolean)
  )];

  const makeAkibartCartEvent = (cart, sections) => {
    const itemCount = getAkibartCartCount(cart);
    const totalPrice = Number(cart?.total_price || 0);
    const currency = cart?.currency || window.Shopify?.currency?.active || 'JPY';
    const event = new Event('shopify:cart:lines-update', { bubbles: true });
    const promise = Promise.resolve({
      cart: {
        totalQuantity: itemCount,
        cost: { totalAmount: { amount: String(totalPrice / 100), currencyCode: currency } },
      },
      detail: {
        sections: sections || undefined,
        items: cart?.items || [],
        itemCount,
        source: 'akibart-cart',
        didError: false,
      },
    });
    Object.assign(event, { action: 'update', context: 'cart', lines: [], promise });
    document.dispatchEvent(event);
    return event;
  };

  const cartMatchesAkibartData = (component, cart) => {
    const expectedItems = Array.isArray(cart?.items) ? cart.items : [];
    const actualRows = [...component.querySelectorAll('tr[data-key]')];
    if (actualRows.length !== expectedItems.length) return false;
    if (expectedItems.length === 0) return component.querySelector('[data-cart-drawer-empty]') !== null;

    return expectedItems.every((item) => {
      const row = actualRows.find((candidate) => candidate.dataset.key === item.key);
      const input = row?.querySelector('input[data-cart-line]');
      return Boolean(
        row && input && Number(input.value) === Number(item.quantity) &&
        Number(row.dataset.finalLinePrice) === Number(item.final_line_price)
      );
    });
  };

  const waitForAkibartCartRender = (component, cart) => new Promise((resolve) => {
    if (!component) return resolve();

    let observer;
    let settleTimer;
    let timeoutTimer;
    let frame = null;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      observer?.disconnect();
      if (settleTimer) window.clearTimeout(settleTimer);
      if (timeoutTimer) window.clearTimeout(timeoutTimer);
      if (frame !== null) window.cancelAnimationFrame(frame);
      resolve();
    };
    const check = () => {
      if (!cartMatchesAkibartData(component, cart) || frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = window.requestAnimationFrame(finish);
      });
    };

    observer = new MutationObserver(check);
    observer.observe(component, { subtree: true, childList: true, attributes: true, characterData: true });
    timeoutTimer = window.setTimeout(finish, 2500);
    check();
  });

  const hasAkibartCartWorkPending = () => (
    akibartPendingCartMutations > 0 ||
    akibartPendingQuantityRequests > 0 ||
    pendingAkibartAddBatches.size > 0 ||
    akibartAddFlushTimer !== null ||
    pendingAkibartQuantityChanges.size > 0 ||
    akibartQuantityFlushTimer !== null
  );

  const refreshAkibartCartAfterMutations = async () => {
    if (hasAkibartCartWorkPending()) return;
    const refreshSequence = ++akibartCartRefreshSequence;
    const mutationVersion = akibartCartMutationVersion;
    const shouldCloseIfEmpty = akibartCloseDrawerOnEmpty;
    akibartCloseDrawerOnEmpty = false;

    try {
      let cart;
      try {
        cart = await fetchAkibartCart();
      } catch (error) {
        if (!akibartCartSnapshot) {
          showAkibartCartToast('カートを最新の状態に更新できませんでした。', 'error');
          throw error;
        }
        cart = akibartCartSnapshot;
      }
      if (refreshSequence !== akibartCartRefreshSequence || mutationVersion !== akibartCartMutationVersion || hasAkibartCartWorkPending()) return;
      akibartCartSnapshot = cart;
      renderAkibartCartCount(getAkibartCartCount(cart));
      const sections = akibartLatestCartSections;
      akibartLatestCartSections = null;
      makeAkibartCartEvent(cart, sections);

      const drawer = document.querySelector('theme-drawer#cart-drawer');
      const drawerCartItems = drawer?.querySelector('cart-items-component[data-drawer]');
      if (drawerCartItems) await waitForAkibartCartRender(drawerCartItems, cart);

      if (
        shouldCloseIfEmpty && getAkibartCartCount(cart) === 0 &&
        refreshSequence === akibartCartRefreshSequence && !hasAkibartCartWorkPending()
      ) {
        drawer?.close?.();
      }
    } catch {
      // Keep the last optimistic state if Shopify's cart endpoint is temporarily unavailable.
    }
  };

  const scheduleAkibartCartRefresh = () => {
    if (hasAkibartCartWorkPending() || akibartCartRefreshTimer !== null) return;
    const mutationVersion = akibartCartMutationVersion;
    akibartCartRefreshTimer = window.setTimeout(() => {
      akibartCartRefreshTimer = null;
      if (mutationVersion !== akibartCartMutationVersion || hasAkibartCartWorkPending()) return;
      void refreshAkibartCartAfterMutations();
    }, 0);
  };

  const markAkibartCartMutationPending = () => {
    akibartCartMutationVersion += 1;
    akibartCartRefreshSequence += 1;
    if (akibartCartRefreshTimer !== null) {
      window.clearTimeout(akibartCartRefreshTimer);
      akibartCartRefreshTimer = null;
    }
  };

  const enqueueAkibartCartMutation = (operation, onSuccess, onError) => {
    markAkibartCartMutationPending();
    akibartPendingCartMutations += 1;

    const request = akibartCartMutationQueue.then(operation, operation);
    akibartCartMutationQueue = request.catch(() => {});
    request.then(
      (value) => {
        try { onSuccess?.(value); } catch (error) { console.error(error); }
      },
      (error) => {
        try { onError?.(error); } catch (callbackError) { console.error(callbackError); }
      }
    ).finally(() => {
      akibartPendingCartMutations = Math.max(0, akibartPendingCartMutations - 1);
      scheduleAkibartCartRefresh();
    });
    return request;
  };

  const getAkibartAddPayload = (formComponent) => {
    const form = formComponent.querySelector('form');
    if (!form) throw new Error('商品情報を取得できませんでした。');
    const formData = new FormData(form);
    const variantId = String(formData.get('id') || '').trim();
    if (!/^\d+$/.test(variantId)) throw new Error('商品情報を取得できませんでした。');
    const parsedId = Number(variantId);
    const quantity = Math.max(1, Number.parseInt(String(formData.get('quantity') || formComponent.dataset.quantityDefault || '1'), 10) || 1);
    const item = { id: Number.isSafeInteger(parsedId) ? parsedId : variantId, quantity };
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const property = key.match(/^properties\[(.+)\]$/);
      if (property && typeof value === 'string' && value !== '') properties[property[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;
    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) item.selling_plan = String(sellingPlan);
    return { item, quantity };
  };

  const markAkibartButtonAdded = (button) => {
    button.removeAttribute('data-akibart-error');
    button.setAttribute('data-akibart-added', 'true');
    const previousTimer = akibartButtonResetTimers.get(button);
    if (previousTimer !== undefined) window.clearTimeout(previousTimer);
    const resetTimer = window.setTimeout(() => {
      button.removeAttribute('data-akibart-added');
      akibartButtonResetTimers.delete(button);
    }, 1200);
    akibartButtonResetTimers.set(button, resetTimer);
  };

  const getAkibartAddBatchKey = (item) => {
    const key = { id: String(item.id) };
    if (item.properties && Object.keys(item.properties).length) {
      key.properties = Object.fromEntries(Object.entries(item.properties).sort(([a], [b]) => a.localeCompare(b)));
    }
    if (item.selling_plan) key.selling_plan = String(item.selling_plan);
    return JSON.stringify(key);
  };

  const flushAkibartAddBatches = () => {
    if (akibartAddFlushTimer !== null) {
      window.clearTimeout(akibartAddFlushTimer);
      akibartAddFlushTimer = null;
    }

    const batches = [...pendingAkibartAddBatches.values()];
    pendingAkibartAddBatches.clear();
    if (!batches.length) {
      scheduleAkibartCartRefresh();
      return;
    }

    batches.forEach((batch) => {
      enqueueAkibartCartMutation(
        async () => {
          try {
            const response = await fetch(getAkibartCartUrl('cart/add.js'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify(batch.item),
            });
            const result = await response.json().catch(() => null);
            if (!response.ok || result?.errors) {
              throw new Error(result?.description || result?.message || result?.errors || 'カートに追加できませんでした。');
            }
            return result;
          } finally {
            invalidateAkibartCartSnapshot();
            akibartLatestCartSections = null;
          }
        },
        () => {
          for (const button of batch.contexts.keys()) {
            if (button.isConnected) markAkibartButtonAdded(button);
          }
          animateAkibartCartIcon();
        },
        (error) => {
          const detail = String(error?.message || '').replace(/\s+/g, ' ').trim().slice(0, 100);
          const message = detail && detail !== 'カートに追加できませんでした。'
            ? `カートに追加できませんでした: ${detail}`
            : 'カートに追加できませんでした。時間をおいて再度お試しください。';
          batch.contexts.forEach((formComponent, button) => showAkibartFormError(formComponent, button, message));
          showAkibartCartToast(message, 'error');
        }
      );
    });
  };

  const queueAkibartAdd = (formComponent, button, item) => {
    const batchKey = getAkibartAddBatchKey(item);
    let batch = pendingAkibartAddBatches.get(batchKey);
    if (!batch) {
      const itemDetails = { ...item };
      delete itemDetails.quantity;
      batch = { item: { ...itemDetails, quantity: 0 }, contexts: new Map() };
      pendingAkibartAddBatches.set(batchKey, batch);
    }

    const quantity = Math.max(1, Number(item.quantity) || 1);
    batch.item.quantity += quantity;
    batch.contexts.set(button, formComponent);
    markAkibartCartMutationPending();
    renderAkibartCartCount(readAkibartDisplayedCartCount(formComponent) + quantity);

    if (akibartAddFlushTimer !== null) window.clearTimeout(akibartAddFlushTimer);
    akibartAddFlushTimer = window.setTimeout(flushAkibartAddBatches, 250);
  };

  const getAkibartCartPath = () => {
    try {
      return new URL(window.Shopify?.routes?.cart_url || '/cart', window.location.origin).pathname.replace(/\/+$/, '') || '/cart';
    } catch {
      return '/cart';
    }
  };

  const isAkibartCartPage = () => window.location.pathname.replace(/\/+$/, '') === getAkibartCartPath();

  const postAkibartCartQuantity = async (change) => {
    const mutationVersion = akibartCartMutationVersion;
    await getAkibartCartSnapshot();

    const sectionIds = getAkibartCartSectionIds();
    const response = await fetch(getAkibartCartUrl('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: change.key,
        quantity: change.quantity,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const result = await response.json().catch(() => null);
    if (!response.ok || result?.errors) {
      const error = typeof result?.errors === 'string' ? result.errors : result?.description || result?.message;
      throw new Error(error || 'カートの数量を更新できませんでした。');
    }

    if (mutationVersion !== akibartCartMutationVersion) {
      invalidateAkibartCartSnapshot();
      akibartLatestCartSections = null;
      return { ...result, sections: null };
    }

    akibartCartSnapshot = result;
    akibartCartSnapshotRevision += 1;
    akibartCartSnapshotPromise = null;
    akibartLatestCartSections = result.sections || null;
    return result;
  };

  const finishAkibartQuantityRequest = () => {
    akibartPendingQuantityRequests = Math.max(0, akibartPendingQuantityRequests - 1);
    scheduleAkibartCartRefresh();
  };

  const flushAkibartQuantityChanges = () => {
    if (akibartQuantityFlushTimer !== null) {
      window.clearTimeout(akibartQuantityFlushTimer);
      akibartQuantityFlushTimer = null;
    }
    const changes = [...pendingAkibartQuantityChanges.values()];
    pendingAkibartQuantityChanges.clear();
    if (!changes.length) {
      scheduleAkibartCartRefresh();
      return;
    }

    changes.forEach((change) => {
      akibartPendingQuantityRequests += 1;
      enqueueAkibartCartMutation(
        () => postAkibartCartQuantity(change),
        (cart) => {
          if (change.quantity === 0) akibartCloseDrawerOnEmpty = true;
          if (cart?.sections) akibartLatestCartSections = cart.sections;
          finishAkibartQuantityRequest();
        },
        (error) => {
          akibartLatestCartSections = null;
          const latestRevision = latestAkibartQuantityRevisions.get(change.key) || change.revision;
          if (latestRevision <= change.revision) {
            restoreAkibartQuantityFromSnapshot(change.component, change.key);
            const detail = String(error?.message || '').replace(/\s+/g, ' ').trim().slice(0, 100);
            showAkibartCartToast(
              detail ? `カートの数量を更新できませんでした: ${detail}` : 'カートの数量を更新できませんでした。時間をおいて再度お試しください。',
              'error'
            );
          }
          finishAkibartQuantityRequest();
        }
      );
    });
  };

  const scheduleAkibartQuantityChange = (change) => {
    const revision = ++akibartQuantityRevision;
    latestAkibartQuantityRevisions.set(change.key, revision);
    pendingAkibartQuantityChanges.set(change.key, { ...change, revision });
    if (akibartQuantityFlushTimer !== null) window.clearTimeout(akibartQuantityFlushTimer);
    akibartQuantityFlushTimer = window.setTimeout(flushAkibartQuantityChanges, 180);
  };

  document.addEventListener('quantity-selector:update', (event) => {
    if (isAkibartCartPage() || !(event.target instanceof Element)) return;
    const selector = event.target.closest('cart-quantity-selector-component');
    const component = selector?.closest('cart-items-component[data-drawer]');
    const input = selector?.querySelector('input[data-cart-line]');
    const row = selector?.closest('tr[data-key]');
    if (!component || !input || !row) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const quantity = Number(event.detail?.quantity);
    if (!Number.isInteger(quantity) || quantity < 0) return;
    updateAkibartOptimisticQuantity(component, row, input, quantity);
    scheduleAkibartQuantityChange({
      component,
      key: row.dataset.key,
      quantity,
    });
  }, true);

  document.addEventListener('submit', (event) => {
    const formComponent = event.target instanceof Element
      ? event.target.closest('product-form-component[data-akibart-cart-feedback]')
      : null;
    if (!formComponent) return;
    const button = formComponent.querySelector('[data-akibart-add-button]');
    if (!button) return;
    // Keep Horizon's CartLinesUpdateEvent out of this add path; the drawer auto-opens on its add action.
    event.preventDefault();
    event.stopImmediatePropagation();
    clearAkibartFormError(formComponent);
    let payload;
    try {
      payload = getAkibartAddPayload(formComponent);
    } catch (error) {
      const message = error?.message || 'カートに追加できませんでした。時間をおいて再度お試しください。';
      showAkibartFormError(formComponent, button, message);
      showAkibartCartToast(message, 'error');
      return;
    }

    const previousTimer = akibartButtonResetTimers.get(button);
    if (previousTimer !== undefined) {
      window.clearTimeout(previousTimer);
      akibartButtonResetTimers.delete(button);
    }
    animateAkibartButtonPress(button);
    animateAkibartFlyToCart(formComponent, button);
    showAkibartCartToast('カートに追加しました');
    queueAkibartAdd(formComponent, button, payload.item);
  }, true);

  const openFilterPanel = (panel, backdrop) => {
    if (!panel) return;
    panel.hidden = false;
    backdrop?.removeAttribute('hidden');
    panel.classList.add('is-open');
    document.documentElement.classList.add('akibart-lock-scroll');
    panel.querySelector('button, input, select')?.focus();
  };

  const closeFilterPanel = (panel, backdrop) => {
    if (!panel) return;
    panel.classList.remove('is-open');
    panel.hidden = true;
    if (backdrop) backdrop.hidden = true;
    document.documentElement.classList.remove('akibart-lock-scroll');
  };

  // A cart update can replace the clicked item before the click bubbles to the
  // dialog. The drawer's outside-click check then sees a detached target and
  // mistakes the interaction for a backdrop click.
  document.querySelectorAll('cart-drawer-component').forEach((cartDrawer) => {
    cartDrawer.addEventListener('click', (event) => {
      if (!(event.target instanceof Element) || event.target.isConnected) return;
      event.stopPropagation();
    });
  });

  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (isAkibartCartPage() || event.action !== 'remove' || !(event.target instanceof Element)) return;
    const cartItems = event.target.closest('cart-items-component[data-drawer]');
    const drawer = cartItems?.closest('theme-drawer#cart-drawer');
    if (!cartItems || !drawer || !event.promise) return;
    akibartCartMutationVersion += 1;
    akibartCartRefreshSequence += 1;
    if (akibartCartRefreshTimer !== null) {
      window.clearTimeout(akibartCartRefreshTimer);
      akibartCartRefreshTimer = null;
    }
    akibartLatestCartSections = null;
    invalidateAkibartCartSnapshot();
    if (!hasAkibartCartWorkPending()) akibartDisplayedCartCount = null;

    event.promise
      .then(async ({ cart, detail }) => {
        invalidateAkibartCartSnapshot();
        if (detail?.didError) return;
        const count = Number(cart?.totalQuantity ?? detail?.itemCount);
        if (Number.isFinite(count) && !hasAkibartCartWorkPending()) renderAkibartCartCount(count);
        if (count !== 0) return;

        await waitForAkibartCartRender(cartItems, { items: Array.isArray(detail?.items) ? detail.items : [] });
        if (
          cartItems.isConnected && cartItems.querySelector('[data-cart-drawer-empty]') &&
          (drawer.isOpen === true || drawer.hasAttribute('open'))
        ) {
          drawer.close?.();
        }
      })
      .catch(() => {});
  });

  document.addEventListener('click', (event) => {
    const favorite = event.target.closest('[data-akibart-favorite]');
    if (favorite) {
      toggleFavorite(favorite.dataset.productHandle);
      return;
    }

    const quantityStep = event.target.closest('[data-akibart-quantity-step]');
    if (quantityStep) {
      const input = quantityStep.closest('.akibart-quantity')?.querySelector('input[name="quantity"]');
      if (input) input.value = String(Math.max(Number(input.min) || 1, Number(input.value || 1) + Number(quantityStep.dataset.akibartQuantityStep)));
      return;
    }

    const galleryButton = event.target.closest('[data-akibart-gallery-image]');
    if (galleryButton) {
      const gallery = galleryButton.closest('[data-akibart-gallery]');
      const mainImage = gallery?.querySelector('[data-akibart-main-image]');
      if (mainImage) {
        mainImage.src = galleryButton.dataset.akibartGalleryImage;
        mainImage.alt = galleryButton.dataset.akibartGalleryAlt || '';
        if (galleryButton.dataset.akibartGalleryZoomImage) {
          mainImage.dataset.akibartMainImageFull = galleryButton.dataset.akibartGalleryZoomImage;
          const lightboxImage = gallery?.querySelector('[data-akibart-lightbox-image]');
          if (lightboxImage) {
            lightboxImage.src = galleryButton.dataset.akibartGalleryZoomImage;
            lightboxImage.alt = mainImage.alt;
          }
        }
      }
      gallery?.querySelectorAll('[data-akibart-gallery-image]').forEach((button) => {
        if (button.dataset.akibartGalleryImage === galleryButton.dataset.akibartGalleryImage) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      return;
    }

    const openButton = event.target.closest('[data-akibart-filter-open]');
    if (openButton) {
      const section = openButton.closest('.akibart-collection');
      openFilterPanel(section?.querySelector('[data-akibart-filter-panel]'), section?.querySelector('[data-akibart-filter-backdrop]'));
      return;
    }

    const closeButton = event.target.closest('[data-akibart-filter-close], [data-akibart-filter-backdrop]');
    if (closeButton) {
      const section = closeButton.closest('.akibart-collection') || closeButton.parentElement?.closest('.akibart-collection');
      closeFilterPanel(section?.querySelector('[data-akibart-filter-panel]'), section?.querySelector('[data-akibart-filter-backdrop]'));
    }
  });

  document.addEventListener('change', (event) => {
    const sorter = event.target.closest('[data-akibart-sort]');
    if (sorter) sorter.form?.requestSubmit();

    const variant = event.target.closest('[data-akibart-variant]');
    if (variant && variant.tagName === 'SELECT') {
      const option = variant.selectedOptions[0];
      const product = variant.closest('form')?.closest('.akibart-product__information');
      const price = product?.querySelector('[data-akibart-product-price]');
      const submit = product?.querySelector('[data-akibart-add-button]');
      if (price && option?.dataset.price) price.innerHTML = `${option.dataset.price} <small>（税込）</small>`;
      if (submit) {
        const isAvailable = option?.dataset.available === 'true';
        submit.disabled = !isAvailable;
        const label = isAvailable ? submit.dataset.labelAvailable : submit.dataset.labelUnavailable;
        if (label) submit.textContent = label;
      }
    }
  });

  document.addEventListener('akibart:favorites-change', () => {
    setFavoriteState();
    document.querySelectorAll('[data-akibart-favorites]').forEach((section) => renderFavoritesPage(section));
  });

  window.addEventListener('storage', (event) => {
    if (event.key === storageKey) {
      setFavoriteState();
      document.querySelectorAll('[data-akibart-favorites]').forEach((section) => renderFavoritesPage(section));
    }
  });

  const newsletterForm = document.getElementById('AkibartNewsletterForm');
  if (newsletterForm) {
    let isNewsletterSubmitting = false;
    const submitButton = newsletterForm.querySelector('button[type="submit"]');
    const submitButtonMarkup = submitButton?.innerHTML || '';

    const setNewsletterSubmitting = (submitting) => {
      isNewsletterSubmitting = submitting;
      newsletterForm.toggleAttribute('aria-busy', submitting);

      const currentSubmitButton = newsletterForm.querySelector('button[type="submit"]');
      if (!currentSubmitButton) return;

      currentSubmitButton.disabled = submitting;
      currentSubmitButton.innerHTML = submitting ? '送信中…' : submitButtonMarkup;
    };

    const clearNewsletterError = () => {
      newsletterForm.querySelector('#AkibartNewsletterError')?.remove();
      const emailInput = newsletterForm.querySelector('[name="contact[email]"]');
      if (!emailInput) return;

      emailInput.removeAttribute('aria-invalid');
      const describedBy = (emailInput.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter((id) => id && id !== 'AkibartNewsletterError');
      if (describedBy.length > 0) {
        emailInput.setAttribute('aria-describedby', describedBy.join(' '));
      } else {
        emailInput.removeAttribute('aria-describedby');
      }
    };

    const showNewsletterError = (message) => {
      let errorMessage = newsletterForm.querySelector('#AkibartNewsletterError');
      if (!errorMessage) {
        errorMessage = document.createElement('p');
        errorMessage.id = 'AkibartNewsletterError';
        errorMessage.className = 'akibart-newsletter__message akibart-newsletter__message--error';
        errorMessage.setAttribute('role', 'alert');
        newsletterForm.append(errorMessage);
      }
      errorMessage.textContent = message;

      const emailInput = newsletterForm.querySelector('[name="contact[email]"]');
      if (emailInput) {
        emailInput.setAttribute('aria-invalid', 'true');
        const describedBy = (emailInput.getAttribute('aria-describedby') || '')
          .split(/\s+/)
          .filter(Boolean);
        if (!describedBy.includes(errorMessage.id)) describedBy.push(errorMessage.id);
        emailInput.setAttribute('aria-describedby', describedBy.join(' '));
      }
    };

    const isNewsletterChallenge = (responseDocument, responseUrl) => {
      let responsePath = '';
      try {
        responsePath = new URL(responseUrl || newsletterForm.action, window.location.href).pathname.toLowerCase();
      } catch (_error) {
        // The response markup below can still identify a challenge page.
      }
      if (responsePath.includes('/challenge')) return true;

      const challengeMarkup = responseDocument.querySelector(
        'form#challenge-form, form[action*="/challenge"], #shopify-challenge, .shopify-challenge__container, [data-shopify-challenge], #cf-challenge-running, #challenge-stage, input[name="cf-turnstile-response"]',
      );
      const title = responseDocument.title || '';
      const bodyText = (responseDocument.body?.textContent || '').slice(0, 2000);
      return Boolean(
        challengeMarkup
        || /challenge|just a moment|security verification|verify you are human/i.test(title)
        || /checking your browser|verify you are human|complete the security check/i.test(bodyText),
      );
    };

    newsletterForm.addEventListener('submit', async (event) => {
      if (isNewsletterSubmitting) {
        event.preventDefault();
        return;
      }

      if (typeof window.fetch !== 'function' || typeof window.FormData !== 'function' || typeof window.DOMParser !== 'function') {
        return;
      }

      event.preventDefault();
      clearNewsletterError();
      const formData = new FormData(newsletterForm);
      setNewsletterSubmitting(true);

      try {
        const response = await window.fetch(newsletterForm.action, {
          method: 'POST',
          body: formData,
          credentials: 'same-origin',
          headers: { Accept: 'text/html, application/xhtml+xml' },
        });
        const responseHtml = await response.text();
        const responseDocument = new DOMParser().parseFromString(responseHtml, 'text/html');

        if (isNewsletterChallenge(responseDocument, response.url)) {
          HTMLFormElement.prototype.submit.call(newsletterForm);
          return;
        }

        const responseForm = responseDocument.getElementById(newsletterForm.id);
        const successCard = responseForm?.querySelector('.akibart-newsletter__success');
        if (successCard) {
          const insertedSuccessCard = document.importNode(successCard, true);
          newsletterForm.replaceChildren(insertedSuccessCard);
          setNewsletterSubmitting(false);
          insertedSuccessCard.focus({ preventScroll: true });
          return;
        }

        const responseError = responseForm?.querySelector(
          '.akibart-newsletter__message--error, [aria-invalid="true"]',
        );
        if (responseForm && responseError) {
          newsletterForm.replaceChildren(
            ...Array.from(responseForm.childNodes, (node) => document.importNode(node, true)),
          );
          setNewsletterSubmitting(false);
          return;
        }

        setNewsletterSubmitting(false);
        showNewsletterError('送信に失敗しました。通信状況をご確認のうえ、再度お試しください。');
      } catch (_error) {
        setNewsletterSubmitting(false);
        showNewsletterError('送信に失敗しました。通信状況をご確認のうえ、再度お試しください。');
      }
    });
  }

  document.querySelectorAll('[data-akibart-hero-slider]').forEach(initializeSlider);
  document.querySelectorAll('[data-akibart-gallery]').forEach(initializeGallery);
  document.querySelectorAll('[data-akibart-carousel]').forEach(initializeCarousel);
  setFavoriteState();
  document.querySelectorAll('[data-akibart-favorites]').forEach((section) => renderFavoritesPage(section));
})();
